import React, { useEffect, useState, useRef } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Stack,
  CircularProgress,
  Box,
  Button,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  ClickAwayListener,
  OutlinedInput,
  ListItemText,
  FormControlLabel,
  Switch,
  Menu,
  SvgIcon,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useApi } from '../api/client';
import { Page } from '../components/Page';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

/** Closed eye (lid + lashes) — used when zero-amount rows are hidden. */
function ClosedEyeOutlinedIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2 8a10.65 10.65 0 0 0 20 0M4 15l1.73-2.05M9 18l.72-3.25M15 18l-.72-3.25M20 15l-1.73-2.05"
      />
    </SvgIcon>
  );
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Tab type lookup. financeTypes is Array<{ name, type, hidden? }>. Type 'marriage' enables payment-mode + paid-by; 'plan' uses expense cards with savings. */
function getTabType(name, financeTypes) {
  if (typeof name !== 'string' || !Array.isArray(financeTypes)) return 'generic';
  const lc = name.trim().toLowerCase();
  for (const t of financeTypes) {
    if (t && typeof t.name === 'string' && t.name.toLowerCase() === lc) return t.type;
  }
  return 'generic';
}

function isTabHidden(name, financeTypes) {
  if (typeof name !== 'string' || !Array.isArray(financeTypes)) return false;
  const lc = name.trim().toLowerCase();
  for (const t of financeTypes) {
    if (t && typeof t.name === 'string' && t.name.toLowerCase() === lc) return t.hidden === true;
  }
  return false;
}

function normalizeCustomTabType(type) {
  if (type === 'marriage' || type === 'plan' || type === 'generic') return type;
  return 'generic';
}

function formatRupees(amount) {
  const n = Number(amount);
  if (Number.isNaN(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/**
 * Effective date the cash must be ready for a plan expense.
 * Cash: the expense date itself. Credit card (default): the bill generates on the 25th and is
 * payable on the 15th of the following month — expenses on/before the 25th land on that
 * month's bill (due the 15th of month+1), later ones on the next bill (due the 15th of month+2).
 * Returns 'YYYY-MM-DD' or null.
 */
function planCashOutDate(dateStr, paymentMode) {
  if (!dateStr) return null;
  const iso = String(dateStr).slice(0, 10);
  const parts = iso.split('-');
  if (parts.length < 3) return null;
  const y = Number(parts[0]);
  const mo = Number(parts[1]);
  const day = Number(parts[2]);
  if (!y || !mo || !day) return null;
  if (paymentMode === 'cash') return iso;
  const monthShift = day <= 25 ? 1 : 2;
  const totalMonths = y * 12 + (mo - 1) + monthShift;
  const dueYear = Math.floor(totalMonths / 12);
  const dueMonth = (totalMonths % 12) + 1;
  return `${dueYear}-${String(dueMonth).padStart(2, '0')}-15`;
}

/**
 * Number of salary events (1st of the month) between today (exclusive) and dateStr (inclusive).
 * Money is saved on the 1st, so this is how many monthly saves can fund the expense.
 * 0 = no upcoming 1st lands before the money is needed (must come from cash in hand).
 */
function monthStartsUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(String(dateStr) + 'T00:00:00');
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  const diff = (target.getFullYear() - today.getFullYear()) * 12 + (target.getMonth() - today.getMonth());
  return Math.max(0, diff);
}

function planSavedForExpense(expenseName, incomeRows) {
  const key = expenseName && String(expenseName).trim() ? String(expenseName).trim().toLowerCase() : '';
  if (!key || !Array.isArray(incomeRows)) return { amount: 0, rows: [] };
  const rows = [];
  let amount = 0;
  for (const r of incomeRows) {
    const n = r && r.name != null ? String(r.name).trim().toLowerCase() : '';
    if (n !== key) continue;
    rows.push(r);
    amount += Number(r.amount) || 0;
  }
  return { amount, rows };
}

function monthlySavingsNeeded(expenseAmount, savedAmount, dateStr, paymentMode) {
  const remaining = Math.max(0, (Number(expenseAmount) || 0) - (Number(savedAmount) || 0));
  if (remaining <= 0) return { remaining: 0, months: null, monthly: 0, fullyFunded: true, cashOutDate: null };
  const cashOutDate = planCashOutDate(dateStr, paymentMode);
  const months = monthStartsUntil(cashOutDate);
  if (months == null) return { remaining, months: null, monthly: null, fullyFunded: false, cashOutDate };
  if (months === 0) return { remaining, months: 0, monthly: remaining, fullyFunded: false, cashOutDate };
  return { remaining, months, monthly: remaining / months, fullyFunded: false, cashOutDate };
}

/**
 * Build the cheapest month-by-month savings schedule that still meets every goal's due date.
 * Strategy: find the minimum feasible monthly cap, then each month pay the larger of
 * (a) what is strictly required to stay feasible under that cap, (b) the configured
 * monthly floor (minMonthly), and (c) remaining / months-left (smooth target), funding
 * earliest deadlines first. (b)+(c) prepay future goals in light months so leftover does
 * not spike into the final month; the cap keeps deferrable goals from stacking EMIs.
 * goals: [{ id, name, remaining, months }] where months >= 0 (calendar months until due, 0 = due now).
 * Returns { peak, slots: [{ monthOffset, total, parts: [{ id, name, amount }] }] } where
 * peak is the largest scheduled month.
 */
function buildPlanSchedule(goals, minMonthly) {
  const floor = Math.max(0, Number(minMonthly) || 0);
  const items = [];
  for (const g of goals) {
    const remaining = Math.round(Number(g.remaining) || 0);
    if (remaining <= 0) continue;
    items.push({ id: g.id, name: g.name, remaining, due: Math.max(1, Number(g.months) || 0) });
  }
  if (items.length === 0) return { peak: 0, slots: [] };
  items.sort((a, b) => a.due - b.due);
  // Minimum feasible monthly cap: max over deadlines of (cumulative need due by t) / t.
  let cap = 0;
  let cum = 0;
  for (const it of items) {
    cum += it.remaining;
    const rate = cum / it.due;
    if (rate > cap) cap = rate;
  }
  const slots = [];
  for (let m = 0; m < 600; m += 1) {
    let totalRemaining = 0;
    for (const it of items) totalRemaining += it.remaining;
    if (totalRemaining <= 0) break;
    // Minimum payment this month so every future month can stay <= cap.
    let payNeeded = 0;
    let cumNow = 0;
    let horizon = 1;
    for (const it of items) {
      if (it.remaining <= 0) continue;
      const rel = Math.max(1, it.due - m);
      if (rel > horizon) horizon = rel;
      cumNow += it.remaining;
      const req = cumNow - (rel - 1) * cap;
      if (req > payNeeded) payNeeded = req;
    }
    // Light months: at least floor, and spread remaining evenly over the horizon so
    // the last month does not absorb a spike that earlier months could have prepaid.
    const smoothTarget = totalRemaining / horizon;
    let pay = Math.max(Math.max(0, payNeeded), Math.max(floor, smoothTarget));
    pay = Math.round(pay);
    if (horizon === 1 || pay > totalRemaining) pay = totalRemaining;
    if (pay <= 0) break;
    const parts = [];
    let toPay = pay;
    for (const it of items) {
      if (toPay <= 0) break;
      if (it.remaining <= 0) continue;
      const a = Math.min(it.remaining, toPay);
      it.remaining -= a;
      toPay -= a;
      parts.push({ id: it.id, name: it.name, amount: a });
    }
    slots.push({ monthOffset: m, total: pay, parts });
  }
  let peak = 0;
  for (const s of slots) {
    if (s.total > peak) peak = s.total;
  }
  return { peak, slots };
}

/** Label for schedule slot m (0-based): the 1st of the (m+1)-th upcoming month, e.g. "1 Aug 2026". */
function planSlotLabel(monthOffset) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + monthOffset + 1);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatPaymentModeLabel(mode) {
  if (mode === 'credit_card') return 'Credit card';
  if (mode === 'cash') return 'Cash';
  return '—';
}

/** Day-of-month / grace integer from API row (avoids showing — for valid 0). */
function financeIntField(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}
function formatMonthOption(yyyyMm) {
  if (!yyyyMm) return '';
  const [y, m] = yyyyMm.split('-').map(Number);
  return `${MONTH_LABELS[m - 1]} ${y}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getDate()} ${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "31st" / "Cash needed by 30th" / 30 → 30 */
function parseOrdinalDay(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const m = String(value).match(/(\d+)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * Month-wrap day order for cash-needed / due: 30, 31, 1, 2, …, 29.
 * ASC uses this ascending; DESC reverses via the normal sort sign.
 */
function monthWrapDaySortKey(day) {
  const d = Number(day) || 0;
  if (d >= 30) return d - 30; // 30 → 0, 31 → 1
  return d + 1; // 1 → 2, 2 → 3, …, 29 → 30
}

function cashNeededSortKey(item) {
  let day = parseOrdinalDay(item.byDay);
  if (day == null) day = parseOrdinalDay(item.label);
  const dayKey = monthWrapDaySortKey(day != null ? day : 0);
  // Prev-month (-1) and current (0) share one cycle; next month (1+) comes after.
  const m = Number(item.byMonthOffset) || 0;
  const monthBucket = m > 0 ? m : 0;
  return monthBucket * 100 + dayKey;
}

function sortCashNeededBy(items) {
  const out = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (Number(item.amount) > 0) out.push(item);
  }
  out.sort((a, b) => {
    const ka = cashNeededSortKey(a);
    const kb = cashNeededSortKey(b);
    if (ka < kb) return -1;
    if (ka > kb) return 1;
    return 0;
  });
  return out;
}

/** Normalized category key for row identity (matches backend aggregate keys). */
function financesCategoryKey(c) {
  if (c == null) return '';
  return String(c).trim();
}

const LS_COLS_PERSONAL_INCOME = 'finances.columns.personalIncome';
const LS_COLS_PERSONAL_EXPENSE = 'finances.columns.personalExpense.v2';
const LS_COLS_CUSTOM_INCOME = 'finances.columns.customIncome';
const LS_COLS_CUSTOM_EXPENSE = 'finances.columns.customExpense.v2';
const LS_SHOW_ZERO_INCOME = 'finances.showZero.income.v1';
const LS_SHOW_ZERO_EXPENSE = 'finances.showZero.expense.v1';

const DEFAULT_COLS_PERSONAL_INCOME = { name: true, amount: true, category: false };
const DEFAULT_COLS_PERSONAL_EXPENSE = {
  name: true,
  amount: true,
  due: false,
  category: true,
  billDate: false,
  graceDays: false,
};
const DEFAULT_COLS_CUSTOM_INCOME = { date: true, name: true, amount: true, category: false };
const DEFAULT_COLS_CUSTOM_EXPENSE = {
  date: false,
  name: true,
  amount: true,
  category: true,
  billDate: false,
  graceDays: false,
  paymentMode: true,
  paidBy: true,
};

function loadFinanceColumnPrefs(storageKey, defaults) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...defaults };
    return { ...defaults, ...parsed };
  } catch {
    return { ...defaults };
  }
}

function loadFinanceBoolPref(storageKey, defaultValue) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null) return defaultValue;
    if (raw === 'true' || raw === '1') return true;
    if (raw === 'false' || raw === '0') return false;
    return defaultValue;
  } catch {
    return defaultValue;
  }
}

function filterRowsByAmount(rows, showZeroAmountRows) {
  if (showZeroAmountRows) return rows;
  const out = [];
  for (const row of rows) {
    if ((Number(row.amount) || 0) !== 0) out.push(row);
  }
  return out;
}

/** Multi-select column visibility; `prefs` is e.g. { name: true, amount: true, category: false } */
function countVisibleFromPrefs(prefs, ids) {
  let n = 0;
  for (let i = 0; i < ids.length; i += 1) {
    if (prefs[ids[i]]) n += 1;
  }
  return n;
}

function FinanceColumnMenu({ labelId, label, prefs, onPrefsChange, options }) {
  const selectedIds = [];
  for (let i = 0; i < options.length; i += 1) {
    const opt = options[i];
    if (prefs[opt.id]) selectedIds.push(opt.id);
  }

  return (
    <FormControl size="small" sx={{ minWidth: 160, maxWidth: 280 }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        multiple
        value={selectedIds}
        onChange={(e) => {
          const next = { ...prefs };
          const vals = e.target.value;
          const setOn = {};
          for (let j = 0; j < vals.length; j += 1) {
            setOn[vals[j]] = true;
          }
          for (let k = 0; k < options.length; k += 1) {
            const id = options[k].id;
            next[id] = Boolean(setOn[id]);
          }
          onPrefsChange(next);
        }}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => {
          const labels = [];
          for (let i = 0; i < options.length; i += 1) {
            const opt = options[i];
            if (selected.indexOf(opt.id) >= 0) labels.push(opt.label);
          }
          return labels.join(', ');
        }}
        MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            <Checkbox checked={Boolean(prefs[opt.id])} size="small" sx={{ py: 0, pr: 1 }} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/** sortValue: '<column>-asc' | '<column>-desc' */
function toggleFinanceSort(current, column) {
  const ascKey = `${column}-asc`;
  const descKey = `${column}-desc`;
  if (current === ascKey) return descKey;
  if (current === descKey) return ascKey;
  return ascKey;
}

function financeRowSortValue(row, column) {
  if (column === 'date') return row.date || '';
  if (column === 'name') return String(row.name || '').toLowerCase();
  if (column === 'category') return financesCategoryKey(row.category).toLowerCase();
  if (column === 'amount') return Number(row.amount);
  if (column === 'due') {
    let day = parseOrdinalDay(row.dueDateDisplay);
    if (day == null) day = parseOrdinalDay(row.expense_due_date);
    if (day == null) return null;
    return monthWrapDaySortKey(day);
  }
  if (column === 'paymentMode') return row.payment_mode || '';
  if (column === 'paidBy') return String(row.payment_by || '').toLowerCase();
  if (column === 'billDate') {
    if (row.credit_card_bill_date == null || row.credit_card_bill_date === '') return null;
    return Number(row.credit_card_bill_date);
  }
  if (column === 'graceDays') {
    if (row.interest_free_days == null || row.interest_free_days === '') return null;
    return Number(row.interest_free_days);
  }
  return '';
}

function compareFinanceSortValues(a, b, column) {
  if (column === 'amount' || column === 'due' || column === 'billDate' || column === 'graceDays') {
    const aMissing = a == null || (column === 'amount' && Number.isNaN(a));
    const bMissing = b == null || (column === 'amount' && Number.isNaN(b));
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    const na = Number(a);
    const nb = Number(b);
    if (na < nb) return -1;
    if (na > nb) return 1;
    return 0;
  }
  if (column === 'date') {
    const sa = String(a || '');
    const sb = String(b || '');
    if (!sa && !sb) return 0;
    if (!sa) return 1;
    if (!sb) return -1;
    return sa.localeCompare(sb);
  }
  return String(a || '').localeCompare(String(b || ''));
}

function sortFinanceRows(rows, sortValue) {
  const dash = sortValue.indexOf('-');
  const column = dash >= 0 ? sortValue.slice(0, dash) : 'name';
  const dir = sortValue.endsWith('-desc') ? 'desc' : 'asc';
  const sign = dir === 'desc' ? -1 : 1;
  const secondaryColumn = column === 'amount' ? 'name' : 'amount';
  const out = rows.slice();
  out.sort((a, b) => {
    const va = financeRowSortValue(a, column);
    const vb = financeRowSortValue(b, column);
    let c = compareFinanceSortValues(va, vb, column);
    if (c !== 0) return sign * c;
    const vsa = financeRowSortValue(a, secondaryColumn);
    const vsb = financeRowSortValue(b, secondaryColumn);
    c = compareFinanceSortValues(vsa, vsb, secondaryColumn);
    if (c !== 0) return c;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
  return out;
}

const SORT_HEADER_CELL_SX = {
  cursor: 'pointer',
  userSelect: 'none',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'action.hover' },
};

function SortableHeaderCell({ column, label, sortValue, setSort, align, sx }) {
  const active = sortValue.startsWith(`${column}-`);
  const asc = sortValue.endsWith('-asc');
  const arrow = active ? (asc ? '↑' : '↓') : '↕';
  const cellSx = sx ? { ...SORT_HEADER_CELL_SX, ...sx } : SORT_HEADER_CELL_SX;

  return (
    <TableCell
      align={align}
      onClick={() => setSort((prev) => toggleFinanceSort(prev, column))}
      sx={cellSx}
    >
      {align === 'right' ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            width: '100%',
          }}
        >
          <Box component="span">{label}</Box>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              minWidth: '1em',
              textAlign: 'center',
              opacity: active ? 1 : 0.45,
              fontSize: '0.95rem',
            }}
          >
            {arrow}
          </Box>
        </Box>
      ) : (
        <>
          {label}
          <Box component="span" sx={{ ml: 0.5, opacity: active ? 1 : 0.45, fontSize: '0.95rem' }}>{arrow}</Box>
        </>
      )}
    </TableCell>
  );
}

function isCreditCardRow(row) {
  return row?.expense_type === 'credit_card';
}

function EditableRow({
  row,
  editingField,
  editName,
  editAmount,
  editCategory,
  editDate,
  editPaymentMode,
  editPaymentBy,
  editDueDay,
  canEdit,
  showStatusCheckbox,
  showDateColumn,
  showPaymentColumns,
  showPaymentModeColumn,
  showPaymentByColumn,
  showNameColumn = true,
  showAmountColumn = true,
  showCategoryColumn = false,
  showBillDateColumn = false,
  showGraceDaysColumn = false,
  showDueColumn = true,
  dueDateDisplay,
  trailingCells,
  onStartEdit,
  onEditNameChange,
  onEditAmountChange,
  onEditCategoryChange,
  onEditDateChange,
  onEditPaymentModeChange,
  onEditPaymentByChange,
  onEditDueDayChange,
  onPaymentModeCommitted,
  onSave,
  onCancel,
  onStatusChange,
  onDelete,
  onOpenEditDialog,
}) {
  const [contextMenu, setContextMenu] = useState(null);
  const statusChecked = row.status === 'completed';
  const hasIds = (row.ids?.length ?? 0) > 0 || row.id != null;
  const editingDate = editingField === 'date';
  const editingName = editingField === 'name';
  const editingAmount = editingField === 'amount';
  const editingCategory = editingField === 'category';
  const editingPaymentMode = editingField === 'payment_mode';
  const editingPaymentBy = editingField === 'payment_by';
  const editingDue = editingField === 'due';
  const isEditing =
    editingDate || editingName || editingAmount || editingCategory || editingPaymentMode || editingPaymentBy || editingDue;
  const canDelete = canEdit && onDelete && hasIds;
  const useDueWidths = showDueColumn && dueDateDisplay !== undefined;
  const payModeVisible = showPaymentModeColumn !== undefined ? showPaymentModeColumn : Boolean(showPaymentColumns);
  const payByVisible = showPaymentByColumn !== undefined ? showPaymentByColumn : Boolean(showPaymentColumns);
  const dueEditable = canEdit && showDueColumn && dueDateDisplay !== undefined && !isCreditCardRow(row);

  return (
    <TableRow
      onContextMenu={
        canEdit && onOpenEditDialog
          ? (e) => {
              e.preventDefault();
              setContextMenu(
                contextMenu == null
                  ? { mouseX: e.clientX + 2, mouseY: e.clientY - 6 }
                  : null
              );
            }
          : undefined
      }
    >
      {showStatusCheckbox ? (
        <TableCell padding="none" sx={{ width: 34, maxWidth: 34, px: 0, verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
          {canDelete ? (
            <IconButton size="small" aria-label="Delete row" onClick={() => onDelete(row)} sx={{ color: 'text.secondary', p: 0.5 }}>
              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : null}
        </TableCell>
      ) : null}
      {showStatusCheckbox ? (
        <TableCell padding="none" sx={{ width: 34, maxWidth: 34, px: 0, verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
          <Checkbox
            size="small"
            checked={statusChecked}
            disabled={!hasIds}
            onChange={(e) => {
              e.stopPropagation();
              if (hasIds) onStatusChange?.(row, e.target.checked);
            }}
            sx={{ p: 0.5 }}
          />
        </TableCell>
      ) : null}
      {showDateColumn ? (
        <TableCell
          padding="none"
          sx={{
            px: 1,
            py: 0.5,
            whiteSpace: 'nowrap',
            ...(canEdit && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }),
          }}
          onClick={canEdit && !isEditing ? () => onStartEdit(row, 'date') : undefined}
        >
          {editingDate ? (
            <ClickAwayListener onClickAway={() => onCancel?.()}>
              <Box onClick={(e) => e.stopPropagation()}>
                <TextField
                  size="small"
                  type="date"
                  value={editDate ?? ''}
                  onChange={(e) => onEditDateChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSave('date');
                    if (e.key === 'Escape' && onCancel) onCancel();
                  }}
                  InputLabelProps={{ shrink: true }}
                  autoFocus
                  sx={{ width: 140 }}
                />
              </Box>
            </ClickAwayListener>
          ) : (
            <Typography variant="body2" color="text.secondary">{formatDate(row.date)}</Typography>
          )}
        </TableCell>
      ) : null}
      {showNameColumn ? (
        <TableCell
          padding="none"
          sx={{
            px: 1,
            py: 0.5,
            ...(useDueWidths && { width: '55%' }),
            ...(canEdit && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }),
          }}
          onClick={canEdit && !isEditing ? () => onStartEdit(row, 'name') : undefined}
        >
          {editingName ? (
            <ClickAwayListener onClickAway={() => onCancel?.()}>
              <Box onClick={(e) => e.stopPropagation()}>
                <TextField
                  size="small"
                  fullWidth
                  value={editName}
                  onChange={(e) => onEditNameChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSave('name');
                    if (e.key === 'Escape' && onCancel) onCancel();
                  }}
                  autoFocus
                />
              </Box>
            </ClickAwayListener>
          ) : (
            row.name
          )}
        </TableCell>
      ) : null}
      {showCategoryColumn ? (
        <TableCell
          padding="none"
          sx={{
            px: 1,
            py: 0.5,
            ...(useDueWidths && { maxWidth: 180 }),
            ...(canEdit && onEditCategoryChange && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }),
          }}
          onClick={canEdit && !isEditing && onEditCategoryChange ? () => onStartEdit(row, 'category') : undefined}
        >
          {editingCategory && onEditCategoryChange ? (
            <ClickAwayListener onClickAway={() => onCancel?.()}>
              <Box onClick={(e) => e.stopPropagation()}>
                <TextField
                  size="small"
                  fullWidth
                  value={editCategory ?? ''}
                  onChange={(e) => onEditCategoryChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSave('category');
                    if (e.key === 'Escape' && onCancel) onCancel();
                  }}
                  autoFocus
                  placeholder="Category"
                />
              </Box>
            </ClickAwayListener>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {row.category != null && String(row.category).trim() ? row.category : '—'}
            </Typography>
          )}
        </TableCell>
      ) : null}
      {showAmountColumn ? (
        <TableCell
          align="right"
          padding="none"
          sx={{
            px: 1,
            py: 0.5,
            ...(useDueWidths && { width: '30%' }),
            ...(canEdit && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }),
          }}
          onClick={canEdit && !isEditing ? () => onStartEdit(row, 'amount') : undefined}
        >
          {editingAmount ? (
            <ClickAwayListener onClickAway={() => onCancel?.()}>
              <Box onClick={(e) => e.stopPropagation()}>
                <TextField
                  size="small"
                  type="number"
                  fullWidth
                  value={editAmount}
                  onChange={(e) => onEditAmountChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSave('amount');
                    if (e.key === 'Escape' && onCancel) onCancel();
                  }}
                  autoFocus
                />
              </Box>
            </ClickAwayListener>
          ) : (
            Number(row.amount).toFixed(2)
          )}
        </TableCell>
      ) : null}
      {payModeVisible ? (
        <TableCell
          padding="none"
          sx={{
            px: 1,
            py: 0.5,
            whiteSpace: 'nowrap',
            ...(canEdit && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }),
          }}
          onClick={canEdit && !isEditing ? () => onStartEdit(row, 'payment_mode') : undefined}
        >
          {editingPaymentMode ? (
            <Box onClick={(e) => e.stopPropagation()} sx={{ minWidth: 130, maxWidth: 200 }}>
              <Box
                component="select"
                autoFocus
                value={editPaymentMode === 'credit_card' ? 'credit_card' : editPaymentMode === 'cash' ? 'cash' : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  onEditPaymentModeChange(v);
                  if (onPaymentModeCommitted) onPaymentModeCommitted(row, v);
                }}
                onBlur={() => {
                  setTimeout(() => onCancel?.(), 0);
                }}
                sx={(theme) => ({
                  display: 'block',
                  width: '100%',
                  fontSize: '0.875rem',
                  fontFamily: theme.typography.fontFamily,
                  py: 1,
                  px: 1,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  cursor: 'pointer',
                })}
              >
                <option value="">Not set</option>
                <option value="cash">Cash</option>
                <option value="credit_card">Credit card</option>
              </Box>
            </Box>
          ) : (
            formatPaymentModeLabel(row.payment_mode)
          )}
        </TableCell>
      ) : null}
      {payByVisible ? (
        <TableCell
          padding="none"
          sx={{
            px: 1,
            py: 0.5,
            ...(canEdit && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }),
          }}
          onClick={canEdit && !isEditing ? () => onStartEdit(row, 'payment_by') : undefined}
        >
          {editingPaymentBy ? (
            <ClickAwayListener onClickAway={() => onCancel?.()}>
              <Box onClick={(e) => e.stopPropagation()}>
                <TextField
                  size="small"
                  fullWidth
                  value={editPaymentBy}
                  onChange={(e) => onEditPaymentByChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSave('payment_by');
                    if (e.key === 'Escape' && onCancel) onCancel();
                  }}
                  autoFocus
                />
              </Box>
            </ClickAwayListener>
          ) : (
            (row.payment_by && String(row.payment_by).trim() ? row.payment_by : '—')
          )}
        </TableCell>
      ) : null}
      {showBillDateColumn ? (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {financeIntField(row.credit_card_bill_date) != null
            ? financeIntField(row.credit_card_bill_date)
            : '—'}
        </TableCell>
      ) : null}
      {showGraceDaysColumn ? (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {financeIntField(row.interest_free_days) != null
            ? financeIntField(row.interest_free_days)
            : '—'}
        </TableCell>
      ) : null}
      {showDueColumn && dueDateDisplay !== undefined && (
        <TableCell
          padding="none"
          sx={{
            whiteSpace: 'nowrap',
            width: '15%',
            px: 1,
            py: 0.5,
            ...(dueEditable && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }),
          }}
          onClick={dueEditable && !isEditing ? () => onStartEdit(row, 'due') : undefined}
        >
          {editingDue && dueEditable && onEditDueDayChange ? (
            <ClickAwayListener onClickAway={() => onCancel?.()}>
              <Box onClick={(e) => e.stopPropagation()}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 31 }}
                  value={editDueDay ?? ''}
                  onChange={(e) => onEditDueDayChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSave('due');
                    if (e.key === 'Escape' && onCancel) onCancel();
                  }}
                  autoFocus
                  sx={{ width: 72 }}
                />
              </Box>
            </ClickAwayListener>
          ) : (
            dueDateDisplay ?? '—'
          )}
        </TableCell>
      )}
      {trailingCells?.map((cell, idx) => (
        <TableCell key={idx} align={cell.align} sx={cell.sx}>{cell.content}</TableCell>
      ))}
      {canEdit && onOpenEditDialog ? (
        <Menu
          open={contextMenu != null}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu != null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem
            onClick={() => {
              setContextMenu(null);
              onOpenEditDialog(row);
            }}
          >
            Edit
          </MenuItem>
        </Menu>
      ) : null}
    </TableRow>
  );
}

export default function Finances() {
  const { request } = useApi();
  const requestRef = useRef(request);
  requestRef.current = request;

  // Finance type tabs — financeTypes is Array<{ name, type }> incl. built-in personal
  const [activeFinanceType, setActiveFinanceType] = useState('personal');
  const [financeTypes, setFinanceTypes] = useState([{ name: 'personal', type: 'personal' }]);
  const [addTabOpen, setAddTabOpen] = useState(false);
  const [addTabName, setAddTabName] = useState('');
  const [addTabType, setAddTabType] = useState('generic');
  const [editTabOpen, setEditTabOpen] = useState(false);
  const [editTabOriginalName, setEditTabOriginalName] = useState('');
  const [editTabName, setEditTabName] = useState('');
  const [editTabType, setEditTabType] = useState('generic');
  const [editTabHidden, setEditTabHidden] = useState(false);
  const [editTabError, setEditTabError] = useState('');
  const [editTabConfirmDelete, setEditTabConfirmDelete] = useState(false);
  const [editTabDeleting, setEditTabDeleting] = useState(false);
  const [hiddenTabsMenuAnchor, setHiddenTabsMenuAnchor] = useState(null);

  // Custom type (non-personal) view state
  const [customEntries, setCustomEntries] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [customReloadTick, setCustomReloadTick] = useState(0);
  const [addCustomIncomeOpen, setAddCustomIncomeOpen] = useState(false);
  const [addCustomExpenseOpen, setAddCustomExpenseOpen] = useState(false);
  const [customIncomeName, setCustomIncomeName] = useState('');
  const [customIncomeAmount, setCustomIncomeAmount] = useState('');
  const [customIncomeDate, setCustomIncomeDate] = useState('');
  const [customIncomeCategory, setCustomIncomeCategory] = useState('');
  const [customExpenseName, setCustomExpenseName] = useState('');
  const [customExpenseAmount, setCustomExpenseAmount] = useState('');
  const [customExpenseDate, setCustomExpenseDate] = useState('');
  const [customExpensePaymentMode, setCustomExpensePaymentMode] = useState('cash');
  const [customExpensePaymentBy, setCustomExpensePaymentBy] = useState('');
  const [customExpenseCategory, setCustomExpenseCategory] = useState('');
  const [customExpenseBillDate, setCustomExpenseBillDate] = useState('');
  const [customExpenseGraceDays, setCustomExpenseGraceDays] = useState('');
  const [planSavedEditExpenseId, setPlanSavedEditExpenseId] = useState(null);
  const [planSavedEditValue, setPlanSavedEditValue] = useState('');
  const [planMinMonthly, setPlanMinMonthly] = useState('50000');
  const [personalIncomeSort, setPersonalIncomeSort] = useState('name-asc');
  const [personalExpenseSort, setPersonalExpenseSort] = useState('name-asc');
  const [customIncomeSort, setCustomIncomeSort] = useState('name-asc');
  const [customExpenseSort, setCustomExpenseSort] = useState('name-asc');

  // Custom view: inline editing state
  const [customEditingRowId, setCustomEditingRowId] = useState(null);
  const [customEditingField, setCustomEditingField] = useState(null);
  const [customEditName, setCustomEditName] = useState('');
  const [customEditAmount, setCustomEditAmount] = useState('');
  const [customEditDate, setCustomEditDate] = useState('');
  const [customEditPaymentMode, setCustomEditPaymentMode] = useState('cash');
  const [customEditPaymentBy, setCustomEditPaymentBy] = useState('');
  const [customEditCategory, setCustomEditCategory] = useState('');

  // Personal view state
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [monthMode, setMonthMode] = useState('dropdown');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [editingRow, setEditingRow] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editName, setEditName] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editDueDay, setEditDueDay] = useState('');
  const [editingFinanceRow, setEditingFinanceRow] = useState(null);
  const [columnPrefsPersonalIncome, setColumnPrefsPersonalIncome] = useState(() =>
    loadFinanceColumnPrefs(LS_COLS_PERSONAL_INCOME, DEFAULT_COLS_PERSONAL_INCOME)
  );
  const [columnPrefsPersonalExpense, setColumnPrefsPersonalExpense] = useState(() =>
    loadFinanceColumnPrefs(LS_COLS_PERSONAL_EXPENSE, DEFAULT_COLS_PERSONAL_EXPENSE)
  );
  const [columnPrefsCustomIncome, setColumnPrefsCustomIncome] = useState(() =>
    loadFinanceColumnPrefs(LS_COLS_CUSTOM_INCOME, DEFAULT_COLS_CUSTOM_INCOME)
  );
  const [columnPrefsCustomExpense, setColumnPrefsCustomExpense] = useState(() =>
    loadFinanceColumnPrefs(LS_COLS_CUSTOM_EXPENSE, DEFAULT_COLS_CUSTOM_EXPENSE)
  );
  const [showZeroIncomeRows, setShowZeroIncomeRows] = useState(() =>
    loadFinanceBoolPref(LS_SHOW_ZERO_INCOME, true)
  );
  const [showZeroExpenseRows, setShowZeroExpenseRows] = useState(() =>
    loadFinanceBoolPref(LS_SHOW_ZERO_EXPENSE, true)
  );
  const [addIncomeOpen, setAddIncomeOpen] = useState(false);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [cloneSeriesOpen, setCloneSeriesOpen] = useState(false);
  const [addIncomeName, setAddIncomeName] = useState('');
  const [addIncomeAmount, setAddIncomeAmount] = useState('');
  const [addIncomeEarner, setAddIncomeEarner] = useState('');
  const [addIncomeStatus, setAddIncomeStatus] = useState('pending');
  const [addExpenseName, setAddExpenseName] = useState('');
  const [addExpenseAmount, setAddExpenseAmount] = useState('');
  const [addExpenseType, setAddExpenseType] = useState('regular');
  const [addExpenseDueDate, setAddExpenseDueDate] = useState('');
  const [addExpenseBillDate, setAddExpenseBillDate] = useState('');
  const [addExpenseInterestFreeDays, setAddExpenseInterestFreeDays] = useState('');
  const [cloneSeriesName, setCloneSeriesName] = useState('');
  const [chartData, setChartData] = useState(null);
  const [subTab, setSubTab] = useState('overview');
  const [taxFy, setTaxFy] = useState('2025-26');
  const [taxSlabs, setTaxSlabs] = useState(null);
  const [taxData, setTaxData] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [addIncomeIncomeType, setAddIncomeIncomeType] = useState('');
  const [addIncomeTds, setAddIncomeTds] = useState('');
  const [addIncomeCategory, setAddIncomeCategory] = useState('');
  const [addExpenseCategory, setAddExpenseCategory] = useState('');
  const [loans, setLoans] = useState([]);
  const [loansLoading, setLoansLoading] = useState(false);
  const [addLoanOpen, setAddLoanOpen] = useState(false);
  const [addLoanName, setAddLoanName] = useState('');
  const [addLoanStartDate, setAddLoanStartDate] = useState('');
  const [addLoanPrincipal, setAddLoanPrincipal] = useState('');
  const [addLoanInterestRate, setAddLoanInterestRate] = useState('');
  const [addLoanTenure, setAddLoanTenure] = useState('');
  const [addLoanEmi, setAddLoanEmi] = useState('');
  const [addLoanForeclosureRate, setAddLoanForeclosureRate] = useState('');
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const skipNextFetch = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(LS_COLS_PERSONAL_INCOME, JSON.stringify(columnPrefsPersonalIncome));
    } catch {
      /* ignore */
    }
  }, [columnPrefsPersonalIncome]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_COLS_PERSONAL_EXPENSE, JSON.stringify(columnPrefsPersonalExpense));
    } catch {
      /* ignore */
    }
  }, [columnPrefsPersonalExpense]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_COLS_CUSTOM_INCOME, JSON.stringify(columnPrefsCustomIncome));
    } catch {
      /* ignore */
    }
  }, [columnPrefsCustomIncome]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_COLS_CUSTOM_EXPENSE, JSON.stringify(columnPrefsCustomExpense));
    } catch {
      /* ignore */
    }
  }, [columnPrefsCustomExpense]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_SHOW_ZERO_INCOME, showZeroIncomeRows ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  }, [showZeroIncomeRows]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_SHOW_ZERO_EXPENSE, showZeroExpenseRows ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  }, [showZeroExpenseRows]);

  // Load finance types on mount
  useEffect(() => {
    let cancelled = false;
    requestRef.current('/api/finances/types')
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json || !Array.isArray(json.types)) return;
        setFinanceTypes(json.types);
      })
      .catch((e) => { if (!cancelled) console.error('Failed to load finance types', e); });
    return () => { cancelled = true; };
  }, []);

  // Load plan settings (monthly floor for the plan schedule) on mount
  useEffect(() => {
    let cancelled = false;
    requestRef.current('/api/finances/plan-settings')
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json || json.min_monthly == null) return;
        setPlanMinMonthly(String(json.min_monthly));
      })
      .catch((e) => { if (!cancelled) console.error('Failed to load plan settings', e); });
    return () => { cancelled = true; };
  }, []);

  // Leave a tab if it becomes hidden
  useEffect(() => {
    if (isTabHidden(activeFinanceType, financeTypes)) {
      setActiveFinanceType('personal');
    }
  }, [financeTypes, activeFinanceType]);

  // Load custom entries when on a non-personal finance type
  useEffect(() => {
    if (activeFinanceType === 'personal') return;
    let cancelled = false;
    setCustomLoading(true);
    requestRef.current(`/api/finances/entries?finances_name=${encodeURIComponent(activeFinanceType)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!cancelled && json) setCustomEntries(json);
      })
      .catch((e) => { if (!cancelled) console.error('Failed to load custom entries', e); })
      .finally(() => { if (!cancelled) setCustomLoading(false); });
    return () => { cancelled = true; };
  }, [activeFinanceType, customReloadTick]);

  // Personal: load finances data
  useEffect(() => {
    if (activeFinanceType !== 'personal') return;
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    const isInitialLoad = from == null && to == null;
    let cancelled = false;
    setLoading(true);
    const req = requestRef.current;
    const params =
      from != null && to != null
        ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        : '';
    req(`/api/finances${params}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!cancelled && json) {
          setData(json);
          if (isInitialLoad && json.latest != null) {
            setFrom(json.latest);
            setTo(json.latest);
            skipNextFetch.current = true;
          }
          setEditingRow(null);
          setEditingField(null);
        }
      })
      .catch((e) => {
        if (!cancelled) console.error('Failed to load finances', e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [from, to, reloadTick, activeFinanceType]);

  useEffect(() => {
    if (activeFinanceType !== 'personal') return;
    if (subTab !== 'tax') return;
    let cancelled = false;
    const req = requestRef.current;
    req('/api/finances/tax-slabs')
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!cancelled && json) setTaxSlabs(json);
      })
      .catch((e) => { if (!cancelled) console.error('Failed to load tax slabs', e); });
    return () => { cancelled = true; };
  }, [subTab, activeFinanceType]);

  useEffect(() => {
    if (activeFinanceType !== 'personal') return;
    if (subTab !== 'tax' || !taxFy) return;
    let cancelled = false;
    setTaxLoading(true);
    const req = requestRef.current;
    req(`/api/finances/tax?fy=${encodeURIComponent(taxFy)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!cancelled && json) setTaxData(json);
      })
      .catch((e) => { if (!cancelled) console.error('Failed to load tax data', e); })
      .finally(() => { if (!cancelled) setTaxLoading(false); });
    return () => { cancelled = true; };
  }, [subTab, taxFy, reloadTick, activeFinanceType]);

  useEffect(() => {
    if (activeFinanceType !== 'personal') return;
    if (subTab !== 'loans') return;
    let cancelled = false;
    setLoansLoading(true);
    requestRef.current('/api/finances/loans')
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!cancelled && json) setLoans(Array.isArray(json) ? json : []);
      })
      .catch((e) => { if (!cancelled) console.error('Failed to load loans', e); })
      .finally(() => { if (!cancelled) setLoansLoading(false); });
    return () => { cancelled = true; };
  }, [subTab, reloadTick, activeFinanceType]);

  useEffect(() => {
    if (activeFinanceType !== 'personal') return;
    if (from == null || to == null) {
      setChartData(null);
      return;
    }
    let cancelled = false;
    const req = requestRef.current;
    const params = `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    req(`/api/finances/charts${params}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!cancelled && json) setChartData(json);
      })
      .catch((e) => {
        if (!cancelled) console.error('Failed to load chart data', e);
      });
    return () => { cancelled = true; };
  }, [from, to, reloadTick, activeFinanceType]);

  // Finance type tab handlers
  function handleFinanceTypeTabChange(_, newVal) {
    if (newVal === '__add__') {
      setAddTabOpen(true);
    } else if (newVal === '__hidden__') {
      return;
    } else if (newVal !== null) {
      setActiveFinanceType(newVal);
    }
  }

  function applyTypesResponse(json) {
    if (!json || !Array.isArray(json.types)) return;
    setFinanceTypes(json.types);
  }

  async function handleAddFinanceType() {
    const name = addTabName.trim();
    if (!name) return;
    const type = normalizeCustomTabType(addTabType);
    try {
      const res = await requestRef.current('/api/finances/types', {
        method: 'POST',
        body: JSON.stringify({ name, type }),
      });
      if (res.ok) {
        const json = await res.json();
        applyTypesResponse(json);
        setActiveFinanceType(name);
        setAddTabOpen(false);
        setAddTabName('');
        setAddTabType('generic');
      }
    } catch (e) {
      console.error('Failed to add finance type', e);
    }
  }

  function openEditTabDialog() {
    if (activeFinanceType === 'personal') return;
    const currentType = getTabType(activeFinanceType, financeTypes);
    setEditTabOriginalName(activeFinanceType);
    setEditTabName(activeFinanceType);
    setEditTabType(normalizeCustomTabType(currentType));
    setEditTabHidden(isTabHidden(activeFinanceType, financeTypes));
    setEditTabError('');
    setEditTabOpen(true);
  }

  function closeEditTabDialog() {
    setEditTabOpen(false);
    setEditTabOriginalName('');
    setEditTabName('');
    setEditTabType('generic');
    setEditTabHidden(false);
    setEditTabError('');
    setEditTabConfirmDelete(false);
    setEditTabDeleting(false);
  }

  async function handleDeleteTab() {
    if (!editTabOriginalName) return;
    if (editTabOriginalName.toLowerCase() === 'personal') return;
    setEditTabDeleting(true);
    setEditTabError('');
    try {
      const res = await requestRef.current(`/api/finances/types/${encodeURIComponent(editTabOriginalName)}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        const json = await res.json();
        applyTypesResponse(json);
        if (activeFinanceType === editTabOriginalName) setActiveFinanceType('personal');
        setCustomReloadTick((tick) => tick + 1);
        closeEditTabDialog();
        return;
      }
      let body = null;
      try { body = await res.json(); } catch { /* ignore */ }
      setEditTabError((body && body.error) ? body.error : 'Failed to delete tab');
    } catch (e) {
      console.error('Failed to delete finance type', e);
      setEditTabError('Failed to delete tab');
    } finally {
      setEditTabDeleting(false);
    }
  }

  async function handleSaveEditTab() {
    const newName = editTabName.trim();
    if (!newName) {
      setEditTabError('Name is required');
      return;
    }
    if (newName.toLowerCase() === 'personal') {
      setEditTabError('Cannot use "personal" as a tab name');
      return;
    }
    const newType = normalizeCustomTabType(editTabType);
    if (newName !== editTabOriginalName) {
      for (const t of financeTypes) {
        if (t.name.toLowerCase() === editTabOriginalName.toLowerCase()) continue;
        if (t.name.toLowerCase() === newName.toLowerCase()) {
          setEditTabError('Another tab already uses this name');
          return;
        }
      }
    }
    try {
      const res = await requestRef.current(`/api/finances/types/${encodeURIComponent(editTabOriginalName)}`, {
        method: 'PUT',
        body: JSON.stringify({ name: newName, type: newType, hidden: editTabHidden }),
      });
      if (res.ok) {
        const json = await res.json();
        applyTypesResponse(json);
        if (editTabHidden) {
          if (activeFinanceType === editTabOriginalName || activeFinanceType === newName) {
            setActiveFinanceType('personal');
          }
        } else if (activeFinanceType === editTabOriginalName) {
          setActiveFinanceType(newName);
        }
        setCustomReloadTick((tick) => tick + 1);
        closeEditTabDialog();
        return;
      }
      let body = null;
      try { body = await res.json(); } catch { /* ignore */ }
      if (res.status === 409) setEditTabError('Another tab already uses this name');
      else if (body && body.error) setEditTabError(body.error);
      else setEditTabError('Failed to save changes');
    } catch (e) {
      console.error('Failed to update finance type', e);
      setEditTabError('Failed to save changes');
    }
  }

  async function handleUnhideTab(tabName) {
    setHiddenTabsMenuAnchor(null);
    const name = typeof tabName === 'string' ? tabName.trim() : '';
    if (!name) return;
    try {
      const res = await requestRef.current(`/api/finances/types/${encodeURIComponent(name)}`, {
        method: 'PUT',
        body: JSON.stringify({ hidden: false }),
      });
      if (res.ok) {
        const json = await res.json();
        applyTypesResponse(json);
        setActiveFinanceType(name);
        setCustomReloadTick((tick) => tick + 1);
      }
    } catch (e) {
      console.error('Failed to unhide finance tab', e);
    }
  }

  // Custom type: add/edit income
  async function handleAddCustomIncome() {
    const name = customIncomeName.trim();
    const amount = parseFloat(customIncomeAmount);
    if (!name || Number.isNaN(amount)) return;
    const category = customIncomeCategory.trim() ? customIncomeCategory.trim() : null;
    const date = customIncomeDate || null;
    try {
      if (editingFinanceRow?.kind === 'custom-income') {
        await saveRowEditDialog(editingFinanceRow.row, { name, amount, date, category });
      } else {
        const res = await requestRef.current('/api/finances', {
          method: 'POST',
          body: JSON.stringify({
            type: 'income',
            name,
            amount,
            finances_type: getTabType(activeFinanceType, financeTypes),
            finances_name: activeFinanceType,
            date: date || undefined,
            status: 'pending',
            category: category || undefined,
          }),
        });
        if (!res.ok) return;
        setCustomReloadTick((t) => t + 1);
      }
      resetCustomIncomeForm();
    } catch (e) {
      console.error('Failed to save custom income', e);
      setCustomReloadTick((t) => t + 1);
    }
  }

  function applyCustomExpensePaymentFields(target) {
    const tabType = getTabType(activeFinanceType, financeTypes);
    if (tabType === 'marriage') {
      target.payment_mode = customExpensePaymentMode === 'credit_card' ? 'credit_card' : 'cash';
      target.payment_by = customExpensePaymentBy.trim() || null;
    } else if (tabType === 'plan') {
      target.payment_mode = customExpensePaymentMode === 'cash' ? 'cash' : 'credit_card';
    }
  }

  function applyCustomExpenseBillGraceFields(target) {
    if (customExpenseBillDate === '') {
      target.credit_card_bill_date = null;
    } else {
      const n = parseInt(customExpenseBillDate, 10);
      target.credit_card_bill_date = Number.isFinite(n) ? Math.min(31, Math.max(1, n)) : null;
    }
    if (customExpenseGraceDays === '') {
      target.interest_free_days = null;
    } else {
      const n = parseInt(customExpenseGraceDays, 10);
      target.interest_free_days = Number.isFinite(n) ? Math.max(0, n) : null;
    }
  }

  // Custom type: add/edit expense
  async function handleAddCustomExpense() {
    const name = customExpenseName.trim();
    const amount = parseFloat(customExpenseAmount);
    if (!name || Number.isNaN(amount)) return;
    const category = customExpenseCategory.trim() ? customExpenseCategory.trim() : null;
    const date = customExpenseDate || null;
    try {
      if (editingFinanceRow?.kind === 'custom-expense') {
        const body = { name, amount, date, category };
        applyCustomExpensePaymentFields(body);
        applyCustomExpenseBillGraceFields(body);
        await saveRowEditDialog(editingFinanceRow.row, body);
      } else {
        const payload = {
          type: 'expenses',
          name,
          amount,
          finances_type: getTabType(activeFinanceType, financeTypes),
          finances_name: activeFinanceType,
          date: date || undefined,
          status: 'pending',
        };
        applyCustomExpensePaymentFields(payload);
        applyCustomExpenseBillGraceFields(payload);
        if (category) payload.category = category;
        const res = await requestRef.current('/api/finances', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (!res.ok) return;
        setCustomReloadTick((t) => t + 1);
      }
      resetCustomExpenseForm();
    } catch (e) {
      console.error('Failed to save custom expense', e);
      setCustomReloadTick((t) => t + 1);
    }
  }

  async function handleDeleteCustomRow(rowId) {
    const isPlan = getTabType(activeFinanceType, financeTypes) === 'plan';
    let expenseNameToClear = null;
    const savingsIdsToDelete = [];
    if (isPlan && customEntries) {
      for (const r of customEntries.expenses) {
        if (r.id === rowId) {
          expenseNameToClear = r.name;
          break;
        }
      }
      if (expenseNameToClear) {
        const matched = planSavedForExpense(expenseNameToClear, customEntries.income);
        for (const r of matched.rows) savingsIdsToDelete.push(r.id);
      }
    }
    setCustomEntries((prev) => {
      if (!prev) return prev;
      const newIncome = [];
      for (const r of prev.income) {
        let skip = false;
        for (const sid of savingsIdsToDelete) {
          if (r.id === sid) {
            skip = true;
            break;
          }
        }
        if (skip || r.id === rowId) continue;
        newIncome.push(r);
      }
      const newExpenses = [];
      for (const r of prev.expenses) {
        if (r.id !== rowId) newExpenses.push(r);
      }
      return { ...prev, income: newIncome, expenses: newExpenses };
    });
    try {
      const res = await requestRef.current(`/api/finances/${rowId}`, { method: 'DELETE' });
      if (!res.ok) {
        setCustomReloadTick((t) => t + 1);
        return;
      }
      for (const sid of savingsIdsToDelete) {
        try {
          await requestRef.current(`/api/finances/${sid}`, { method: 'DELETE' });
        } catch (e) {
          console.error('Failed to delete plan savings row', e);
        }
      }
    } catch (e) {
      console.error('Failed to delete custom row', e);
      setCustomReloadTick((t) => t + 1);
    }
  }

  async function savePlanSavedAmount(expense) {
    if (!expense || !customEntries) return;
    const amount = parseFloat(planSavedEditValue);
    if (Number.isNaN(amount) || amount < 0) return;
    const name = expense.name && String(expense.name).trim() ? String(expense.name).trim() : '';
    if (!name) return;
    const matched = planSavedForExpense(name, customEntries.income);
    setPlanSavedEditExpenseId(null);
    setPlanSavedEditValue('');
    try {
      if (matched.rows.length === 0) {
        if (amount <= 0) return;
        await requestRef.current('/api/finances', {
          method: 'POST',
          body: JSON.stringify({
            type: 'income',
            name,
            amount,
            finances_type: 'plan',
            finances_name: activeFinanceType,
            status: 'completed',
          }),
        });
        setCustomReloadTick((t) => t + 1);
        return;
      }
      const primary = matched.rows[0];
      if (amount <= 0) {
        for (const r of matched.rows) {
          await requestRef.current(`/api/finances/${r.id}`, { method: 'DELETE' });
        }
        setCustomReloadTick((t) => t + 1);
        return;
      }
      const patchRes = await requestRef.current(`/api/finances/${primary.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ amount }),
      });
      if (!patchRes.ok) {
        setCustomReloadTick((t) => t + 1);
        return;
      }
      for (let i = 1; i < matched.rows.length; i += 1) {
        await requestRef.current(`/api/finances/${matched.rows[i].id}`, { method: 'DELETE' });
      }
      setCustomReloadTick((t) => t + 1);
    } catch (e) {
      console.error('Failed to save plan saved amount', e);
      setCustomReloadTick((t) => t + 1);
    }
  }

  async function handleCustomStatusChange(rowId, checked) {
    const status = checked ? 'completed' : 'pending';
    setCustomEntries((prev) => {
      if (!prev) return prev;
      const newIncome = [];
      for (const r of prev.income) {
        newIncome.push(r.id === rowId ? { ...r, status } : r);
      }
      const newExpenses = [];
      for (const r of prev.expenses) {
        newExpenses.push(r.id === rowId ? { ...r, status } : r);
      }
      return { ...prev, income: newIncome, expenses: newExpenses };
    });
    try {
      const res = await requestRef.current(`/api/finances/${rowId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) setCustomReloadTick((t) => t + 1);
    } catch (e) {
      console.error('Failed to update custom row status', e);
      setCustomReloadTick((t) => t + 1);
    }
  }

  async function handlePlanPaymentModeChange(rowId, mode) {
    const paymentMode = mode === 'cash' ? 'cash' : 'credit_card';
    setCustomEntries((prev) => {
      if (!prev) return prev;
      const newExpenses = [];
      for (const r of prev.expenses) {
        newExpenses.push(r.id === rowId ? { ...r, payment_mode: paymentMode } : r);
      }
      return { ...prev, expenses: newExpenses };
    });
    try {
      const res = await requestRef.current(`/api/finances/${rowId}`, {
        method: 'PATCH',
        body: JSON.stringify({ payment_mode: paymentMode }),
      });
      if (!res.ok) setCustomReloadTick((t) => t + 1);
    } catch (e) {
      console.error('Failed to update plan payment mode', e);
      setCustomReloadTick((t) => t + 1);
    }
  }

  async function savePlanMinMonthly() {
    const n = parseFloat(planMinMonthly);
    const value = Number.isFinite(n) && n >= 0 ? n : 0;
    setPlanMinMonthly(String(value));
    try {
      const res = await requestRef.current('/api/finances/plan-settings', {
        method: 'PUT',
        body: JSON.stringify({ min_monthly: value }),
      });
      if (!res.ok) console.error('Failed to save plan settings');
    } catch (e) {
      console.error('Failed to save plan settings', e);
    }
  }

  async function handlePlanIncludeInSummaryChange(rowId, checked) {
    setCustomEntries((prev) => {
      if (!prev) return prev;
      const newExpenses = [];
      for (const r of prev.expenses) {
        newExpenses.push(r.id === rowId ? { ...r, include_in_summary: checked } : r);
      }
      return { ...prev, expenses: newExpenses };
    });
    try {
      const res = await requestRef.current(`/api/finances/${rowId}`, {
        method: 'PATCH',
        body: JSON.stringify({ include_in_summary: checked }),
      });
      if (!res.ok) setCustomReloadTick((t) => t + 1);
    } catch (e) {
      console.error('Failed to update plan include in summary', e);
      setCustomReloadTick((t) => t + 1);
    }
  }

  function startCustomEdit(row, field) {
    setCustomEditingRowId(row.id);
    setCustomEditingField(field);
    setCustomEditName(row.name);
    setCustomEditAmount(String(row.amount));
    setCustomEditDate(row.date ?? '');
    setCustomEditCategory(row.category != null ? String(row.category) : '');
    if (row.payment_mode === 'credit_card') setCustomEditPaymentMode('credit_card');
    else if (row.payment_mode === 'cash') setCustomEditPaymentMode('cash');
    else setCustomEditPaymentMode('');
    setCustomEditPaymentBy(row.payment_by != null ? String(row.payment_by) : '');
  }

  function cancelCustomEdit() {
    setCustomEditingRowId(null);
    setCustomEditingField(null);
  }

  async function saveCustomEdit(rowId, field, overridePaymentMode, overridePaymentBy) {
    const body = {};
    let oldExpenseName = null;
    if (field === 'name') {
      const name = customEditName.trim();
      if (!name) return;
      body.name = name;
      if (getTabType(activeFinanceType, financeTypes) === 'plan' && customEntries) {
        for (const r of customEntries.expenses) {
          if (r.id === rowId) {
            oldExpenseName = r.name;
            break;
          }
        }
      }
    } else if (field === 'amount') {
      const amount = parseFloat(customEditAmount);
      if (Number.isNaN(amount)) return;
      body.amount = amount;
    } else if (field === 'date') {
      body.date = customEditDate || null;
    } else if (field === 'payment_mode') {
      const mode = overridePaymentMode !== undefined ? overridePaymentMode : customEditPaymentMode;
      if (mode === '' || mode == null) {
        body.payment_mode = null;
      } else {
        body.payment_mode = mode === 'credit_card' ? 'credit_card' : 'cash';
      }
    } else if (field === 'payment_by') {
      const raw = overridePaymentBy !== undefined ? overridePaymentBy : customEditPaymentBy;
      const trimmed = typeof raw === 'string' ? raw.trim() : '';
      body.payment_by = trimmed ? trimmed : null;
    } else if (field === 'category') {
      const trimmed = customEditCategory.trim();
      body.category = trimmed ? trimmed : null;
    }
    setCustomEditingRowId(null);
    setCustomEditingField(null);
    setCustomEntries((prev) => {
      if (!prev) return prev;
      const applyUpdate = (r) => {
        if (r.id !== rowId) return r;
        const updated = { ...r };
        if (field === 'name') updated.name = body.name;
        else if (field === 'amount') updated.amount = body.amount;
        else if (field === 'date') updated.date = body.date;
        else if (field === 'payment_mode') updated.payment_mode = body.payment_mode;
        else if (field === 'payment_by') updated.payment_by = body.payment_by;
        else if (field === 'category') updated.category = body.category;
        return updated;
      };
      const newIncome = [];
      for (const r of prev.income) {
        if (
          field === 'name' &&
          oldExpenseName &&
          body.name &&
          r.name != null &&
          String(r.name).trim().toLowerCase() === String(oldExpenseName).trim().toLowerCase()
        ) {
          newIncome.push({ ...r, name: body.name });
          continue;
        }
        newIncome.push(applyUpdate(r));
      }
      const newExpenses = [];
      for (const r of prev.expenses) newExpenses.push(applyUpdate(r));
      return { ...prev, income: newIncome, expenses: newExpenses };
    });
    try {
      const res = await requestRef.current(`/api/finances/${rowId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setCustomReloadTick((t) => t + 1);
        return;
      }
      if (
        field === 'name' &&
        oldExpenseName &&
        body.name &&
        String(oldExpenseName).trim().toLowerCase() !== String(body.name).trim().toLowerCase() &&
        customEntries
      ) {
        const matched = planSavedForExpense(oldExpenseName, customEntries.income);
        for (const r of matched.rows) {
          try {
            await requestRef.current(`/api/finances/${r.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ name: body.name }),
            });
          } catch (e) {
            console.error('Failed to rename plan savings row', e);
          }
        }
        setCustomReloadTick((t) => t + 1);
      }
    } catch (e) {
      console.error('Failed to save custom row edit', e);
      setCustomReloadTick((t) => t + 1);
    }
  }

  // Personal view handlers
  async function handleAddLoan() {
    const name = addLoanName.trim();
    const start_date = addLoanStartDate;
    const principal = parseFloat(addLoanPrincipal);
    const interest_rate = parseFloat(addLoanInterestRate);
    const tenure = parseInt(addLoanTenure, 10);
    const foreclosure_rate = addLoanForeclosureRate.trim() === '' ? undefined : parseFloat(addLoanForeclosureRate);
    const emi = addLoanEmi.trim() === '' ? undefined : parseFloat(addLoanEmi);
    if (!name || !start_date || Number.isNaN(principal) || principal < 0 || Number.isNaN(interest_rate) || interest_rate < 0 || !Number.isInteger(tenure) || tenure < 1) return;
    try {
      const body = { name, start_date, principal, interest_rate, tenure_in_months: tenure };
      if (foreclosure_rate != null && !Number.isNaN(foreclosure_rate) && foreclosure_rate >= 0) body.foreclosure_rate = foreclosure_rate;
      if (emi != null && !Number.isNaN(emi) && emi > 0) body.emi = emi;
      const res = await requestRef.current('/api/finances/loans', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setAddLoanOpen(false);
        setAddLoanName('');
        setAddLoanStartDate('');
        setAddLoanPrincipal('');
        setAddLoanInterestRate('');
        setAddLoanTenure('');
        setAddLoanEmi('');
        setAddLoanForeclosureRate('');
        setReloadTick((t) => t + 1);
      }
    } catch (e) {
      console.error('Failed to add loan', e);
    }
  }

  async function openLedger(loanId, loanName) {
    setLedgerOpen(true);
    setLedgerData(null);
    setLedgerLoading(true);
    try {
      const res = await requestRef.current(`/api/finances/loans/${loanId}/ledger`);
      const json = res.ok ? await res.json() : null;
      if (json) setLedgerData({ ...json, loanName });
    } catch (e) {
      console.error('Failed to load ledger', e);
    } finally {
      setLedgerLoading(false);
    }
  }

  async function deleteLoan(loanId) {
    if (!window.confirm('Delete this loan? This cannot be undone.')) return;
    try {
      const res = await requestRef.current(`/api/finances/loans/${loanId}`, { method: 'DELETE' });
      if (res.ok) setReloadTick((t) => t + 1);
    } catch (e) {
      console.error('Failed to delete loan', e);
    }
  }

  async function saveEdit(field) {
    if (editingRow == null || !field) return;
    const ids = editingRow.ids || (editingRow.id != null ? [editingRow.id] : []);
    if (ids.length === 0) {
      setEditingRow(null);
      setEditingField(null);
      return;
    }
    const body = {};
    if (field === 'name') {
      const name = editName.trim();
      if (name === '') return;
      body.name = name;
    } else if (field === 'amount') {
      const amount = parseFloat(editAmount);
      if (Number.isNaN(amount)) return;
      body.amount = amount;
    } else if (field === 'category') {
      const trimmed = editCategory.trim();
      body.category = trimmed ? trimmed : null;
    } else if (field === 'due') {
      if (isCreditCardRow(editingRow)) return;
      if (editDueDay === '') {
        body.expense_due_date = null;
      } else {
        const n = parseInt(editDueDay, 10);
        if (!Number.isFinite(n) || n < 1 || n > 31) return;
        body.expense_due_date = n;
      }
    } else {
      return;
    }
    try {
      const req = requestRef.current;
      for (const id of ids) {
        const res = await req(`/api/finances/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error('Update failed');
      }
      setEditingRow(null);
      setEditingField(null);
      setReloadTick((t) => t + 1);
    } catch (e) {
      console.error('Failed to update', e);
    }
  }

  function startEdit(row, field) {
    setEditingRow(row);
    setEditingField(field);
    setEditName(row.name);
    setEditAmount(String(row.amount));
    setEditCategory(row.category != null ? String(row.category) : '');
    setEditDueDay(
      row.expense_due_date != null && row.expense_due_date !== ''
        ? String(row.expense_due_date)
        : ''
    );
  }

  function cancelEdit() {
    setEditingRow(null);
    setEditingField(null);
  }

  async function saveRowEditDialog(row, body) {
    const ids = row.ids || (row.id != null ? [row.id] : []);
    if (ids.length === 0) throw new Error('missing_ids');
    const req = requestRef.current;
    for (const id of ids) {
      const res = await req(`/api/finances/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Update failed');
    }
    if (Array.isArray(row.ids)) {
      setReloadTick((t) => t + 1);
    } else {
      setCustomReloadTick((t) => t + 1);
    }
  }

  function resetPersonalIncomeForm() {
    setAddIncomeOpen(false);
    setAddIncomeName('');
    setAddIncomeAmount('');
    setAddIncomeEarner('');
    setAddIncomeStatus('pending');
    setAddIncomeIncomeType('');
    setAddIncomeTds('');
    setAddIncomeCategory('');
    setEditingFinanceRow(null);
  }

  function resetPersonalExpenseForm() {
    setAddExpenseOpen(false);
    setAddExpenseName('');
    setAddExpenseAmount('');
    setAddExpenseType('regular');
    setAddExpenseDueDate('');
    setAddExpenseBillDate('');
    setAddExpenseInterestFreeDays('');
    setAddExpenseCategory('');
    setEditingFinanceRow(null);
  }

  function resetCustomIncomeForm() {
    setAddCustomIncomeOpen(false);
    setCustomIncomeName('');
    setCustomIncomeAmount('');
    setCustomIncomeDate('');
    setCustomIncomeCategory('');
    setEditingFinanceRow(null);
  }

  function resetCustomExpenseForm() {
    setAddCustomExpenseOpen(false);
    setCustomExpenseName('');
    setCustomExpenseAmount('');
    setCustomExpenseDate('');
    setCustomExpensePaymentMode('cash');
    setCustomExpensePaymentBy('');
    setCustomExpenseCategory('');
    setCustomExpenseBillDate('');
    setCustomExpenseGraceDays('');
    setEditingFinanceRow(null);
  }

  function openEditPersonalIncome(row) {
    setEditingFinanceRow({ row, kind: 'personal-income' });
    setAddIncomeName(row.name != null ? String(row.name) : '');
    setAddIncomeAmount(row.amount != null ? String(row.amount) : '');
    setAddIncomeCategory(row.category != null ? String(row.category) : '');
    setAddIncomeEarner(row.earner != null ? String(row.earner) : '');
    setAddIncomeIncomeType(row.income_type != null ? String(row.income_type) : '');
    setAddIncomeTds(row.tds != null && row.tds !== '' ? String(row.tds) : '');
    setAddIncomeStatus(row.status === 'completed' ? 'completed' : 'pending');
    setAddIncomeOpen(true);
  }

  function openEditPersonalExpense(row) {
    setEditingFinanceRow({ row, kind: 'personal-expense' });
    setAddExpenseName(row.name != null ? String(row.name) : '');
    setAddExpenseAmount(row.amount != null ? String(row.amount) : '');
    setAddExpenseCategory(row.category != null ? String(row.category) : '');
    const bill = financeIntField(row.credit_card_bill_date);
    const grace = financeIntField(row.interest_free_days);
    const isCc =
      row.expense_type === 'credit_card' || bill != null || grace != null;
    setAddExpenseType(isCc ? 'credit_card' : 'regular');
    setAddExpenseDueDate(
      financeIntField(row.expense_due_date) != null ? String(financeIntField(row.expense_due_date)) : ''
    );
    setAddExpenseBillDate(bill != null ? String(bill) : '');
    setAddExpenseInterestFreeDays(grace != null ? String(grace) : '');
    setAddExpenseOpen(true);
  }

  function openEditCustomIncome(row) {
    setEditingFinanceRow({ row, kind: 'custom-income' });
    setCustomIncomeName(row.name != null ? String(row.name) : '');
    setCustomIncomeAmount(row.amount != null ? String(row.amount) : '');
    setCustomIncomeDate(row.date != null ? String(row.date).slice(0, 10) : '');
    setCustomIncomeCategory(row.category != null ? String(row.category) : '');
    setAddCustomIncomeOpen(true);
  }

  function openEditCustomExpense(row) {
    setEditingFinanceRow({ row, kind: 'custom-expense' });
    setCustomExpenseName(row.name != null ? String(row.name) : '');
    setCustomExpenseAmount(row.amount != null ? String(row.amount) : '');
    setCustomExpenseDate(row.date != null ? String(row.date).slice(0, 10) : '');
    setCustomExpenseCategory(row.category != null ? String(row.category) : '');
    if (row.payment_mode === 'credit_card') setCustomExpensePaymentMode('credit_card');
    else if (row.payment_mode === 'cash') setCustomExpensePaymentMode('cash');
    else setCustomExpensePaymentMode('cash');
    setCustomExpensePaymentBy(row.payment_by != null ? String(row.payment_by) : '');
    const bill = financeIntField(row.credit_card_bill_date);
    const grace = financeIntField(row.interest_free_days);
    setCustomExpenseBillDate(bill != null ? String(bill) : '');
    setCustomExpenseGraceDays(grace != null ? String(grace) : '');
    setAddCustomExpenseOpen(true);
  }

  function rowKey(row) {
    if (row.pending && row.ids?.length) return `pending-${row.ids[0]}`;
    if (row.ids) return `${row.type}-${row.name}-${financesCategoryKey(row.category)}`;
    return String(row.id);
  }

  function getEditingFieldForRow(row) {
    if (!editingRow || !editingField) return null;
    if (editingRow.pending && row.pending && editingRow.ids && row.ids) return editingRow.ids[0] === row.ids[0] ? editingField : null;
    if (editingRow.ids && row.ids) {
      const catMatch = financesCategoryKey(editingRow.category) === financesCategoryKey(row.category);
      return editingRow.type === row.type && editingRow.name === row.name && catMatch ? editingField : null;
    }
    return editingRow.id === row.id ? editingField : null;
  }

  async function updateStatus(row, checked) {
    const status = checked ? 'completed' : 'pending';
    const ids = row.ids ?? (row.id != null ? [row.id] : []);
    if (ids.length === 0) return;
    try {
      const req = requestRef.current;
      for (const id of ids) {
        const res = await req(`/api/finances/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status }),
        });
        if (!res.ok) throw new Error('Update failed');
      }
      setReloadTick((t) => t + 1);
    } catch (e) {
      console.error('Failed to update status', e);
    }
  }

  async function handleDelete(row) {
    const ids = row.ids ?? (row.id != null ? [row.id] : []);
    if (ids.length === 0) return;
    try {
      const req = requestRef.current;
      for (const id of ids) {
        const res = await req(`/api/finances/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Delete failed');
      }
      setReloadTick((t) => t + 1);
    } catch (e) {
      console.error('Failed to delete row', e);
    }
  }

  function applyPersonalExpenseTypeFields(target, { forCreate }) {
    if (addExpenseType === 'credit_card') {
      target.expense_type = 'credit_card';
      if (!forCreate) target.expense_due_date = null;
    } else {
      const dueDay = addExpenseDueDate === '' ? null : Math.min(31, Math.max(1, parseInt(addExpenseDueDate, 10)));
      if (forCreate) {
        if (dueDay != null) target.expense_due_date = dueDay;
      } else {
        target.expense_type = 'regular';
        target.expense_due_date = Number.isFinite(dueDay) ? dueDay : null;
      }
    }
    if (addExpenseBillDate === '') {
      if (!forCreate) target.credit_card_bill_date = null;
    } else {
      const billDate = Math.min(31, Math.max(1, parseInt(addExpenseBillDate, 10)));
      if (Number.isFinite(billDate)) target.credit_card_bill_date = billDate;
    }
    if (addExpenseInterestFreeDays === '') {
      if (!forCreate) target.interest_free_days = null;
    } else {
      const days = Math.max(0, parseInt(addExpenseInterestFreeDays, 10));
      if (Number.isFinite(days)) target.interest_free_days = days;
    }
  }

  async function handleAddIncome() {
    if (!from || from !== to) return;
    const name = addIncomeName.trim();
    const amount = parseFloat(addIncomeAmount);
    if (!name || Number.isNaN(amount)) return;
    const tdsRaw = addIncomeTds.trim() === '' ? null : parseFloat(addIncomeTds);
    const tds = tdsRaw != null && !Number.isNaN(tdsRaw) ? tdsRaw : null;
    const fields = {
      name,
      amount,
      earner: addIncomeEarner.trim() || null,
      status: addIncomeStatus,
      income_type: addIncomeIncomeType.trim() || null,
      tds,
      category: addIncomeCategory.trim() ? addIncomeCategory.trim() : null,
    };
    try {
      if (editingFinanceRow?.kind === 'personal-income') {
        await saveRowEditDialog(editingFinanceRow.row, fields);
      } else {
        const res = await requestRef.current('/api/finances', {
          method: 'POST',
          body: JSON.stringify({
            series: from,
            type: 'income',
            name: fields.name,
            amount: fields.amount,
            earner: fields.earner || undefined,
            status: fields.status,
            income_type: fields.income_type || undefined,
            tds: fields.tds != null ? fields.tds : undefined,
            finances_type: 'personal',
            finances_name: 'personal',
            category: fields.category || undefined,
          }),
        });
        if (!res.ok) return;
        setReloadTick((t) => t + 1);
      }
      resetPersonalIncomeForm();
    } catch (e) {
      console.error('Failed to save income', e);
      setReloadTick((t) => t + 1);
    }
  }

  async function handleAddExpense() {
    if (!from || from !== to) return;
    const name = addExpenseName.trim();
    const amount = parseFloat(addExpenseAmount);
    if (!name || Number.isNaN(amount)) return;
    const category = addExpenseCategory.trim() ? addExpenseCategory.trim() : null;
    try {
      if (editingFinanceRow?.kind === 'personal-expense') {
        const body = { name, amount, category };
        applyPersonalExpenseTypeFields(body, { forCreate: false });
        await saveRowEditDialog(editingFinanceRow.row, body);
      } else {
        const payload = {
          series: from,
          type: 'expenses',
          name,
          amount,
          status: 'pending',
          finances_type: 'personal',
          finances_name: 'personal',
        };
        applyPersonalExpenseTypeFields(payload, { forCreate: true });
        if (category) payload.category = category;
        const res = await requestRef.current('/api/finances', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (!res.ok) return;
        setReloadTick((t) => t + 1);
      }
      resetPersonalExpenseForm();
    } catch (e) {
      console.error('Failed to save expense', e);
      setReloadTick((t) => t + 1);
    }
  }

  async function handleCloneSeries() {
    const newSeries = cloneSeriesName.trim();
    if (!newSeries) return;
    try {
      const res = await requestRef.current('/api/finances/clone-series', {
        method: 'POST',
        body: JSON.stringify({ newSeries }),
      });
      if (res.ok) {
        const json = await res.json();
        setCloneSeriesOpen(false);
        setCloneSeriesName('');
        setReloadTick((t) => t + 1);
        if (json.newSeries) {
          const match = newSeries.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(\d{4})$/);
          if (match) {
            const mi = MONTH_LABELS.indexOf(match[1]) + 1;
            const yyyyMm = `${match[2]}-${String(mi).padStart(2, '0')}`;
            setFrom(yyyyMm);
            setTo(yyyyMm);
          }
        }
      }
    } catch (e) {
      console.error('Failed to clone series', e);
    }
  }

  const rowProps = {
    getEditingField: getEditingFieldForRow,
    canEdit: from != null && to != null && from === to,
    showStatusCheckbox: from != null && to != null && from === to,
    editName,
    editAmount,
    editCategory,
    editDueDay,
    onStartEdit: startEdit,
    onEditNameChange: setEditName,
    onEditAmountChange: setEditAmount,
    onEditCategoryChange: setEditCategory,
    onEditDueDayChange: setEditDueDay,
    onSave: saveEdit,
    onCancel: cancelEdit,
    onStatusChange: updateStatus,
  };

  const customRowPropsBase = {
    getEditingField: (row) => (customEditingRowId === row.id ? customEditingField : null),
    canEdit: true,
    showStatusCheckbox: true,
    showDateColumn: true,
    editName: customEditName,
    editAmount: customEditAmount,
    editCategory: customEditCategory,
    editDate: customEditDate,
    editPaymentMode: customEditPaymentMode,
    editPaymentBy: customEditPaymentBy,
    onStartEdit: startCustomEdit,
    onEditNameChange: setCustomEditName,
    onEditAmountChange: setCustomEditAmount,
    onEditCategoryChange: setCustomEditCategory,
    onEditDateChange: setCustomEditDate,
    onEditPaymentModeChange: setCustomEditPaymentMode,
    onEditPaymentByChange: setCustomEditPaymentBy,
    onPaymentModeCommitted: (row, value) => saveCustomEdit(row.id, 'payment_mode', value, undefined),
    onSave: (field) => saveCustomEdit(customEditingRowId, field),
    onCancel: cancelCustomEdit,
    onStatusChange: (row, checked) => handleCustomStatusChange(row.id, checked),
    onDelete: (row) => handleDeleteCustomRow(row.id),
  };

  const activeTabType = getTabType(activeFinanceType, financeTypes);
  const isMarriageTab = activeTabType === 'marriage';
  const isPlanTab = activeTabType === 'plan';
  const engagementTab = isMarriageTab; // legacy alias used in this render
  const customIncomeRowProps = {
    ...customRowPropsBase,
    showPaymentColumns: false,
    showPaymentModeColumn: false,
    showPaymentByColumn: false,
    showDateColumn: columnPrefsCustomIncome.date,
    showNameColumn: columnPrefsCustomIncome.name,
    showAmountColumn: columnPrefsCustomIncome.amount,
    showCategoryColumn: columnPrefsCustomIncome.category,
    onOpenEditDialog: openEditCustomIncome,
  };
  const customExpenseRowProps = {
    ...customRowPropsBase,
    showPaymentColumns: false,
    showPaymentModeColumn: engagementTab && columnPrefsCustomExpense.paymentMode,
    showPaymentByColumn: engagementTab && columnPrefsCustomExpense.paidBy,
    showDateColumn: columnPrefsCustomExpense.date,
    showNameColumn: columnPrefsCustomExpense.name,
    showAmountColumn: columnPrefsCustomExpense.amount,
    showCategoryColumn: columnPrefsCustomExpense.category,
    showBillDateColumn: columnPrefsCustomExpense.billDate,
    showGraceDaysColumn: columnPrefsCustomExpense.graceDays,
    onOpenEditDialog: openEditCustomExpense,
  };
  const customExpenseColSpanIds = ['date', 'name', 'amount', 'category', 'billDate', 'graceDays'];
  if (engagementTab) {
    customExpenseColSpanIds.push('paymentMode');
    customExpenseColSpanIds.push('paidBy');
  }
  const customIncomeColSpan =
    2 + countVisibleFromPrefs(columnPrefsCustomIncome, ['date', 'name', 'category', 'amount']);
  const customExpenseColSpan = 2 + countVisibleFromPrefs(columnPrefsCustomExpense, customExpenseColSpanIds);
  const { getEditingField: getCustomIncomeEditingField, ...customIncomeRowSpread } = customIncomeRowProps;
  const { getEditingField: getCustomExpenseEditingField, ...customExpenseRowSpread } = customExpenseRowProps;

  // ——— Render ———

  const income = data?.income ?? [];
  const expenses = data?.expenses ?? [];
  const cashRows = data?.cash ?? [];
  const pendingFromPrevious = data?.pendingFromPrevious ?? [];
  const pendingExpensesFromPrevious = data?.pendingExpensesFromPrevious ?? [];
  const cashNeededBy = sortCashNeededBy(data?.cashNeededBy ?? []);
  const summary = data?.summary ?? {
    totalIncome: 0,
    totalExpenses: 0,
    cash: 0,
    pendingIncome: 0,
    pendingExpenses: 0,
    pendingIncomeInPeriod: 0,
    pendingExpensesInPeriod: 0,
    incomeByEarner: {},
    incomeByCategory: {},
    expensesByCategory: {},
  };
  const incomeByEarner = summary.incomeByEarner ?? {};
  const incomeByCategory = summary.incomeByCategory ?? {};
  const expensesByCategory = summary.expensesByCategory ?? {};
  const latestMonth = data?.latest ?? null;
  const isLatestMonthView = from != null && to != null && from === to && from === latestMonth;
  const pendingIncome = Number(summary.pendingIncome) || 0;
  const pendingExpenses = Number(summary.pendingExpenses) || 0;
  const pendingIncomeInPeriod = Number(summary.pendingIncomeInPeriod) || 0;
  const pendingExpensesInPeriod = Number(summary.pendingExpensesInPeriod) || 0;
  const totalPendingIncome = (isLatestMonthView ? pendingIncome : 0) + pendingIncomeInPeriod;
  const totalPendingExpenses = (isLatestMonthView ? pendingExpenses : 0) + pendingExpensesInPeriod;
  const balance = Number(summary.cash) + totalPendingIncome - totalPendingExpenses;
  const incomeWithCash = [...income, ...cashRows];
  const personalIncomeSorted = sortFinanceRows(incomeWithCash, personalIncomeSort);
  const personalExpensesSorted = sortFinanceRows(expenses, personalExpenseSort);
  const pendingFromPreviousSorted = sortFinanceRows(pendingFromPrevious, personalIncomeSort);
  const pendingExpensesFromPreviousSorted = sortFinanceRows(pendingExpensesFromPrevious, personalExpenseSort);
  const customIncomeSorted = customEntries ? sortFinanceRows(customEntries.income, customIncomeSort) : [];
  const customExpensesSorted = customEntries
    ? sortFinanceRows(customEntries.expenses, isPlanTab ? 'date-asc' : customExpenseSort)
    : [];
  const visiblePersonalIncomeSorted = filterRowsByAmount(personalIncomeSorted, showZeroIncomeRows);
  const visiblePendingFromPreviousSorted = filterRowsByAmount(pendingFromPreviousSorted, showZeroIncomeRows);
  const visiblePersonalExpensesSorted = filterRowsByAmount(personalExpensesSorted, showZeroExpenseRows);
  const visiblePendingExpensesFromPreviousSorted = filterRowsByAmount(pendingExpensesFromPreviousSorted, showZeroExpenseRows);
  const visibleCustomIncomeSorted = filterRowsByAmount(customIncomeSorted, showZeroIncomeRows);
  const visibleCustomExpensesSorted = filterRowsByAmount(customExpensesSorted, showZeroExpenseRows);
  const canEdit = from != null && to != null && from === to;
  const showStatusCheckbox = canEdit;
  const personalIncomeColSpan =
    (showStatusCheckbox ? 2 : 0) + countVisibleFromPrefs(columnPrefsPersonalIncome, ['name', 'category', 'amount']);
  const personalExpenseColSpan =
    (showStatusCheckbox ? 2 : 0) +
    countVisibleFromPrefs(columnPrefsPersonalExpense, ['name', 'category', 'amount', 'billDate', 'graceDays', 'due']);

  if (activeFinanceType === 'personal' && loading && !data) {
    return (
      <Page>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress />
        </Paper>
      </Page>
    );
  }

  return (
    <Page>
      <Stack spacing={3}>

        {/* Finance type tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          <Tabs
            value={
              financeTypes.some((t) => t.name === activeFinanceType && t.hidden !== true)
                ? activeFinanceType
                : 'personal'
            }
            onChange={handleFinanceTypeTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {(() => {
              const visibleTabEls = [];
              for (const ft of financeTypes) {
                if (ft.hidden === true) continue;
                visibleTabEls.push(
                  <Tab key={ft.name} value={ft.name} label={ft.name.toUpperCase()} />
                );
              }
              return visibleTabEls;
            })()}
          </Tabs>
          {(() => {
            let hiddenCount = 0;
            for (const ft of financeTypes) {
              if (ft.hidden === true && ft.name !== 'personal') hiddenCount += 1;
            }
            if (hiddenCount === 0) return null;
            return (
              <Button
                size="small"
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setHiddenTabsMenuAnchor(e.currentTarget)}
                sx={{ minHeight: 48, flexShrink: 0, px: 1.5, color: 'text.secondary' }}
              >
                Hidden
              </Button>
            );
          })()}
          <Button
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setAddTabOpen(true)}
            sx={{ minHeight: 48, flexShrink: 0, px: 1.5 }}
          >
            Add
          </Button>
          <Menu
            anchorEl={hiddenTabsMenuAnchor}
            open={Boolean(hiddenTabsMenuAnchor)}
            onClose={() => setHiddenTabsMenuAnchor(null)}
          >
            {(() => {
              const items = [];
              for (const ft of financeTypes) {
                if (ft.hidden !== true) continue;
                if (ft.name === 'personal') continue;
                items.push(
                  <MenuItem
                    key={ft.name}
                    onClick={() => handleUnhideTab(ft.name)}
                  >
                    {ft.name}
                  </MenuItem>
                );
              }
              return items;
            })()}
          </Menu>
          {activeFinanceType !== 'personal' && financeTypes.some((t) => t.name === activeFinanceType && t.hidden !== true) && (
            <IconButton
              size="small"
              onClick={openEditTabDialog}
              aria-label="Edit tab"
              sx={{ ml: 0.5, mr: 0.5 }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Add finance type dialog */}
        <Dialog open={addTabOpen} onClose={() => { setAddTabOpen(false); setAddTabName(''); setAddTabType('generic'); }} maxWidth="xs" fullWidth>
          <DialogTitle>Add finance tab</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Tab name"
                value={addTabName}
                onChange={(e) => setAddTabName(e.target.value)}
                fullWidth
                placeholder="e.g. Business, Investments"
                helperText="A new finance tracker tab for a different category"
                onKeyDown={(e) => e.key === 'Enter' && handleAddFinanceType()}
                autoFocus
              />
              <TextField
                select
                label="Tab type"
                value={addTabType}
                onChange={(e) => setAddTabType(e.target.value)}
                fullWidth
                helperText={
                  addTabType === 'marriage'
                    ? 'Adds Payment mode + Paid by columns to expenses (engagement / wedding style).'
                    : addTabType === 'plan'
                      ? 'Expense cards with savings progress and monthly amount needed by the expense date.'
                      : 'Plain income/expense list with date, name, amount, category.'
                }
              >
                <MenuItem value="generic">Generic</MenuItem>
                <MenuItem value="marriage">Marriage (engagement / wedding)</MenuItem>
                <MenuItem value="plan">Plan (savings goals)</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAddTabOpen(false); setAddTabName(''); setAddTabType('generic'); }}>Cancel</Button>
            <Button onClick={handleAddFinanceType} variant="contained" disabled={!addTabName.trim()}>Add</Button>
          </DialogActions>
        </Dialog>

        {/* Edit finance tab dialog */}
        <Dialog open={editTabOpen} onClose={closeEditTabDialog} maxWidth="xs" fullWidth>
          <DialogTitle>{editTabConfirmDelete ? 'Delete finance tab?' : 'Edit finance tab'}</DialogTitle>
          <DialogContent>
            {editTabConfirmDelete ? (
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <Typography variant="body2">
                  This will permanently delete the tab <strong>{editTabOriginalName}</strong> and all of its
                  income and expense entries. This action cannot be undone.
                </Typography>
                {editTabError && (
                  <Typography variant="body2" color="error">{editTabError}</Typography>
                )}
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  label="Tab name"
                  value={editTabName}
                  onChange={(e) => { setEditTabName(e.target.value); if (editTabError) setEditTabError(''); }}
                  fullWidth
                  helperText="Renaming also relabels all existing entries on this tab."
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveEditTab()}
                  autoFocus
                />
                <TextField
                  select
                  label="Tab type"
                  value={editTabType}
                  onChange={(e) => setEditTabType(e.target.value)}
                  fullWidth
                  helperText={
                    editTabType === 'marriage'
                      ? 'Adds Payment mode + Paid by columns to expenses.'
                      : editTabType === 'plan'
                        ? 'Expense cards with savings progress and monthly amount needed by the expense date.'
                        : 'Plain income/expense list with date, name, amount, category.'
                  }
                >
                  <MenuItem value="generic">Generic</MenuItem>
                  <MenuItem value="marriage">Marriage (engagement / wedding)</MenuItem>
                  <MenuItem value="plan">Plan (savings goals)</MenuItem>
                </TextField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editTabHidden}
                      onChange={(e) => setEditTabHidden(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Hide tab"
                />
                {editTabError && (
                  <Typography variant="body2" color="error">{editTabError}</Typography>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            {editTabConfirmDelete ? (
              <>
                <Button onClick={() => { setEditTabConfirmDelete(false); setEditTabError(''); }} disabled={editTabDeleting}>
                  Back
                </Button>
                <Button
                  onClick={handleDeleteTab}
                  color="error"
                  variant="contained"
                  disabled={editTabDeleting}
                  startIcon={<DeleteOutlineIcon />}
                >
                  {editTabDeleting ? 'Deleting…' : 'Delete'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => { setEditTabConfirmDelete(true); setEditTabError(''); }}
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                >
                  Delete
                </Button>
                <Box>
                  <Button onClick={closeEditTabDialog}>Cancel</Button>
                  <Button
                    onClick={handleSaveEditTab}
                    variant="contained"
                    disabled={
                      !editTabName.trim() ||
                      (
                        editTabName.trim() === editTabOriginalName &&
                        editTabType === getTabType(editTabOriginalName, financeTypes) &&
                        editTabHidden === isTabHidden(editTabOriginalName, financeTypes)
                      )
                    }
                    sx={{ ml: 1 }}
                  >
                    Save
                  </Button>
                </Box>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* PERSONAL tab — existing full view */}
        {activeFinanceType === 'personal' && (
          <>
            <Paper sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
              <Stack spacing={{ xs: 2, sm: 2 }} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6">Finances</Typography>
                  <ToggleButtonGroup
                    value={monthMode}
                    exclusive
                    onChange={(_, v) => v && setMonthMode(v)}
                    size="small"
                  >
                    <ToggleButton value="dropdown">Month</ToggleButton>
                    <ToggleButton value="range">Range</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ flex: { sm: '1 1 auto' }, minWidth: 0 }}>
                  {monthMode === 'dropdown' ? (
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                      <InputLabel id="finances-month-label">Month</InputLabel>
                      <Select
                        labelId="finances-month-label"
                        label="Month"
                        value={from ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFrom(v);
                          setTo(v);
                        }}
                      >
                        {(data?.seriesList ?? []).map((yyyyMm) => (
                          <MenuItem key={yyyyMm} value={yyyyMm}>
                            {formatMonthOption(yyyyMm)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <>
                      <TextField
                        label="From"
                        type="month"
                        size="small"
                        value={from ?? ''}
                        onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: '100%', sm: 160 } }}
                      />
                      <TextField
                        label="To"
                        type="month"
                        size="small"
                        value={to ?? ''}
                        onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: '100%', sm: 160 } }}
                      />
                    </>
                  )}
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {latestMonth && (from !== latestMonth || to !== latestMonth) && (
                      <Button variant="outlined" size="small" onClick={() => { setFrom(latestMonth); setTo(latestMonth); }}>
                        Latest month
                      </Button>
                    )}
                    <Button variant="outlined" size="small" onClick={() => setCloneSeriesOpen(true)}>Add new series</Button>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>

            <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab value="overview" label="Overview" />
              <Tab value="tax" label="Tax" />
              <Tab value="loans" label="Loans" />
            </Tabs>

            {subTab === 'overview' && (
              <>
                <Dialog open={addIncomeOpen} onClose={resetPersonalIncomeForm} maxWidth="xs" fullWidth>
                  <DialogTitle>{editingFinanceRow?.kind === 'personal-income' ? 'Edit income' : 'Add income'}</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                      <TextField label="Name" value={addIncomeName} onChange={(e) => setAddIncomeName(e.target.value)} fullWidth required />
                      <TextField label="Amount" type="number" value={addIncomeAmount} onChange={(e) => setAddIncomeAmount(e.target.value)} fullWidth required />
                      <TextField label="Category" value={addIncomeCategory} onChange={(e) => setAddIncomeCategory(e.target.value)} fullWidth placeholder="Optional" />
                      <TextField label="Earner" value={addIncomeEarner} onChange={(e) => setAddIncomeEarner(e.target.value)} fullWidth placeholder="Name of person who earned" />
                      <TextField label="Income type" value={addIncomeIncomeType} onChange={(e) => setAddIncomeIncomeType(e.target.value)} fullWidth placeholder="e.g. contractor (44ADA applied)" helperText="If it contains 'contractor', 50% presumptive income is used for tax" />
                      <TextField label="TDS" type="number" value={addIncomeTds} onChange={(e) => setAddIncomeTds(e.target.value)} fullWidth placeholder="Tax deducted at source" inputProps={{ min: 0, step: 0.01 }} />
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={addIncomeStatus} onChange={(e) => setAddIncomeStatus(e.target.value)}>
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={resetPersonalIncomeForm}>Cancel</Button>
                    <Button onClick={handleAddIncome} variant="contained" disabled={!addIncomeName.trim() || !addIncomeAmount}>
                      {editingFinanceRow?.kind === 'personal-income' ? 'Save' : 'Add'}
                    </Button>
                  </DialogActions>
                </Dialog>

                <Dialog open={addExpenseOpen} onClose={resetPersonalExpenseForm} maxWidth="xs" fullWidth>
                  <DialogTitle>{editingFinanceRow?.kind === 'personal-expense' ? 'Edit expense' : 'Add expense'}</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                      <TextField label="Name" value={addExpenseName} onChange={(e) => setAddExpenseName(e.target.value)} fullWidth required />
                      <TextField label="Amount" type="number" value={addExpenseAmount} onChange={(e) => setAddExpenseAmount(e.target.value)} fullWidth required />
                      <TextField label="Category" value={addExpenseCategory} onChange={(e) => setAddExpenseCategory(e.target.value)} fullWidth placeholder="Optional" />
                      <FormControl fullWidth>
                        <InputLabel>Expense type</InputLabel>
                        <Select label="Expense type" value={addExpenseType} onChange={(e) => setAddExpenseType(e.target.value)}>
                          <MenuItem value="regular">Regular</MenuItem>
                          <MenuItem value="credit_card">Credit card</MenuItem>
                        </Select>
                      </FormControl>
                      {addExpenseType === 'regular' && (
                        <TextField
                          label="Due date (day of month)"
                          type="number"
                          inputProps={{ min: 1, max: 31 }}
                          value={addExpenseDueDate}
                          onChange={(e) => setAddExpenseDueDate(e.target.value)}
                          fullWidth
                          placeholder="1–31"
                          helperText="Optional: day of month when this expense is due"
                        />
                      )}
                      <TextField
                        label="Bill generation date (day)"
                        type="number"
                        inputProps={{ min: 1, max: 31 }}
                        value={addExpenseBillDate}
                        onChange={(e) => setAddExpenseBillDate(e.target.value)}
                        fullWidth
                        placeholder="1–31"
                        helperText={addExpenseType === 'credit_card' ? 'Day of month the bill is generated' : 'Optional'}
                      />
                      <TextField
                        label="Grace period (days)"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={addExpenseInterestFreeDays}
                        onChange={(e) => setAddExpenseInterestFreeDays(e.target.value)}
                        fullWidth
                        placeholder="e.g. 45"
                        helperText={
                          addExpenseType === 'credit_card'
                            ? 'Days after bill date until payment is due'
                            : 'Optional'
                        }
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={resetPersonalExpenseForm}>Cancel</Button>
                    <Button onClick={handleAddExpense} variant="contained" disabled={!addExpenseName.trim() || !addExpenseAmount}>
                      {editingFinanceRow?.kind === 'personal-expense' ? 'Save' : 'Add'}
                    </Button>
                  </DialogActions>
                </Dialog>

                <Dialog open={cloneSeriesOpen} onClose={() => setCloneSeriesOpen(false)} maxWidth="xs" fullWidth>
                  <DialogTitle>Add new series</DialogTitle>
                  <DialogContent>
                    <TextField
                      label="Series name"
                      value={cloneSeriesName}
                      onChange={(e) => setCloneSeriesName(e.target.value)}
                      fullWidth
                      placeholder="e.g. Mar-2026"
                      helperText="Clones all rows from the latest series into this new series"
                      sx={{ pt: 1 }}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setCloneSeriesOpen(false)}>Cancel</Button>
                    <Button onClick={handleCloneSeries} variant="contained" disabled={!cloneSeriesName.trim()}>Clone</Button>
                  </DialogActions>
                </Dialog>

                <Paper sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start', width: '100%' }}>
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Summary</Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" sx={{ overflow: 'auto' }}>
                        <Table size="small" sx={{ width: 'auto', minWidth: { xs: 140, sm: 200 } }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Income</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {totalPendingIncome > 0 && (
                              <TableRow>
                                <TableCell>Pending</TableCell>
                                <TableCell align="right">{totalPendingIncome.toFixed(2)}</TableCell>
                              </TableRow>
                            )}
                            {Object.entries(incomeByEarner).map(([earner, amt]) => (
                              <TableRow key={earner || '_empty'}>
                                <TableCell>{earner ? `${earner}` : 'Others'}</TableCell>
                                <TableCell align="right">{Number(amt).toFixed(2)}</TableCell>
                              </TableRow>
                            ))}
                            {columnPrefsPersonalIncome.category ? (() => {
                              const catKeys = Object.keys(incomeByCategory);
                              catKeys.sort((a, b) => a.localeCompare(b));
                              if (catKeys.length === 0) return null;
                              const rows = [];
                              rows.push(
                                <TableRow key="by-cat-h">
                                  <TableCell colSpan={2} sx={{ fontWeight: 600, pt: 1 }}>By category (all statuses)</TableCell>
                                </TableRow>
                              );
                              for (let ci = 0; ci < catKeys.length; ci += 1) {
                                const k = catKeys[ci];
                                rows.push(
                                  <TableRow key={`icat-${k}`}>
                                    <TableCell sx={{ pl: 2 }}>{k}</TableCell>
                                    <TableCell align="right">{Number(incomeByCategory[k]).toFixed(2)}</TableCell>
                                  </TableRow>
                                );
                              }
                              return <>{rows}</>;
                            })() : null}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Cash</TableCell>
                              <TableCell align="right">{Number(summary.cash).toFixed(2)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{Number(summary.totalIncome).toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                        <Table size="small" sx={{ width: 'auto', minWidth: { xs: 140, sm: 200 } }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Expenses</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {totalPendingExpenses > 0 && (
                              <TableRow>
                                <TableCell>Pending</TableCell>
                                <TableCell align="right">{totalPendingExpenses.toFixed(2)}</TableCell>
                              </TableRow>
                            )}
                            {columnPrefsPersonalExpense.category ? (() => {
                              const catKeys = Object.keys(expensesByCategory);
                              catKeys.sort((a, b) => a.localeCompare(b));
                              if (catKeys.length === 0) return null;
                              const rows = [];
                              rows.push(
                                <TableRow key="by-cat-exp-h">
                                  <TableCell colSpan={2} sx={{ fontWeight: 600, pt: 1 }}>By category (all statuses)</TableCell>
                                </TableRow>
                              );
                              for (let ci = 0; ci < catKeys.length; ci += 1) {
                                const k = catKeys[ci];
                                rows.push(
                                  <TableRow key={`ecat-${k}`}>
                                    <TableCell sx={{ pl: 2 }}>{k}</TableCell>
                                    <TableCell align="right">{Number(expensesByCategory[k]).toFixed(2)}</TableCell>
                                  </TableRow>
                                );
                              }
                              return <>{rows}</>;
                            })() : null}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{Number(summary.totalExpenses).toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Stack>
                    </Box>
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      {from != null && to != null && from === to && cashNeededBy.length > 0 && (
                        <>
                          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Cash needed by</Typography>
                          <Table size="small" sx={{ width: 'auto', minWidth: { xs: 140, sm: 160 } }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Due</TableCell>
                                <TableCell align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {cashNeededBy.map((item, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>{item.label}</TableCell>
                                  <TableCell align="right">{Number(item.amount).toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography component="span" sx={{ fontWeight: 600 }}>Balance</Typography>
                    <Typography component="span" sx={{ ml: 1, fontWeight: 600 }}>{balance.toFixed(2)}</Typography>
                  </Box>
                </Paper>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'stretch', md: 'center' },
                    width: '100%',
                    overflowX: 'auto',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, minmax(min-content, max-content))',
                      },
                      gap: 2,
                      alignItems: 'start',
                      width: { xs: '100%', md: 'max-content' },
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                  <Paper sx={{ overflow: 'visible', width: '100%' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', flexWrap: 'wrap', gap: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="subtitle1">Income</Typography>
                        <IconButton
                          size="small"
                          onClick={() => setShowZeroIncomeRows((v) => !v)}
                          title={showZeroIncomeRows ? 'Hide zero income rows' : 'Show zero income rows'}
                          aria-label={showZeroIncomeRows ? 'Hide zero income rows' : 'Show zero income rows'}
                        >
                          {showZeroIncomeRows ? <VisibilityOutlinedIcon fontSize="small" /> : <ClosedEyeOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <FinanceColumnMenu
                          labelId="fin-cols-personal-income"
                          label="Columns"
                          prefs={columnPrefsPersonalIncome}
                          onPrefsChange={setColumnPrefsPersonalIncome}
                          options={[
                            { id: 'name', label: 'Name' },
                            { id: 'amount', label: 'Amount' },
                            { id: 'category', label: 'Category' },
                          ]}
                        />
                        {canEdit && (
                          <Button size="small" variant="outlined" onClick={() => { setEditingFinanceRow(null); setAddIncomeOpen(true); }}>Add income</Button>
                        )}
                      </Stack>
                    </Stack>
                    <TableContainer sx={{ overflow: 'visible' }}>
                      <Table size="small" sx={{ width: 'max-content', minWidth: '100%' }}>
                        <TableHead>
                          <TableRow>
                            {showStatusCheckbox && <TableCell sx={{ width: 34, maxWidth: 34, px: 0 }} />}
                            {showStatusCheckbox && <TableCell sx={{ width: 34, maxWidth: 34, px: 0 }} />}
                            {columnPrefsPersonalIncome.name ? (
                              <SortableHeaderCell column="name" label="Name" sortValue={personalIncomeSort} setSort={setPersonalIncomeSort} />
                            ) : null}
                            {columnPrefsPersonalIncome.category ? (
                              <SortableHeaderCell column="category" label="Category" sortValue={personalIncomeSort} setSort={setPersonalIncomeSort} />
                            ) : null}
                            {columnPrefsPersonalIncome.amount ? (
                              <SortableHeaderCell column="amount" label="Amount" sortValue={personalIncomeSort} setSort={setPersonalIncomeSort} align="right" />
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visiblePersonalIncomeSorted.map((row) => (
                            <EditableRow
                              key={rowKey(row)}
                              row={row}
                              editingField={rowProps.getEditingField(row)}
                              canEdit={rowProps.canEdit}
                              showStatusCheckbox={rowProps.showStatusCheckbox}
                              showNameColumn={columnPrefsPersonalIncome.name}
                              showAmountColumn={columnPrefsPersonalIncome.amount}
                              showCategoryColumn={columnPrefsPersonalIncome.category}
                              editName={rowProps.editName}
                              editAmount={rowProps.editAmount}
                              editCategory={rowProps.editCategory}
                              onStartEdit={rowProps.onStartEdit}
                              onEditNameChange={rowProps.onEditNameChange}
                              onEditAmountChange={rowProps.onEditAmountChange}
                              onEditCategoryChange={rowProps.onEditCategoryChange}
                              onSave={rowProps.onSave}
                              onCancel={rowProps.onCancel}
                              onStatusChange={rowProps.onStatusChange}
                              onDelete={handleDelete}
                              onOpenEditDialog={openEditPersonalIncome}
                            />
                          ))}
                          {isLatestMonthView && pendingFromPrevious.length > 0 && (
                            <>
                              <TableRow>
                                <TableCell colSpan={personalIncomeColSpan} sx={{ fontWeight: 600, pt: 2, pb: 0.5 }}>Pending from Previous months</TableCell>
                              </TableRow>
                              {visiblePendingFromPreviousSorted.map((row) => (
                                <EditableRow
                                  key={rowKey(row)}
                                  row={row}
                                  editingField={rowProps.getEditingField(row)}
                                  canEdit={rowProps.canEdit}
                                  showStatusCheckbox={rowProps.showStatusCheckbox}
                                  showNameColumn={columnPrefsPersonalIncome.name}
                                  showAmountColumn={columnPrefsPersonalIncome.amount}
                                  showCategoryColumn={columnPrefsPersonalIncome.category}
                                  editName={rowProps.editName}
                                  editAmount={rowProps.editAmount}
                                  editCategory={rowProps.editCategory}
                                  onStartEdit={rowProps.onStartEdit}
                                  onEditNameChange={rowProps.onEditNameChange}
                                  onEditAmountChange={rowProps.onEditAmountChange}
                                  onEditCategoryChange={rowProps.onEditCategoryChange}
                                  onSave={rowProps.onSave}
                                  onCancel={rowProps.onCancel}
                                  onStatusChange={rowProps.onStatusChange}
                                  onDelete={handleDelete}
                                  onOpenEditDialog={openEditPersonalIncome}
                                />
                              ))}
                            </>
                          )}
                          {visiblePersonalIncomeSorted.length === 0 && !(isLatestMonthView && visiblePendingFromPreviousSorted.length > 0) && (
                            <TableRow>
                              <TableCell colSpan={personalIncomeColSpan} sx={{ color: 'text.secondary' }}>
                                No income in range
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  <Paper sx={{ overflow: 'visible', width: '100%' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', flexWrap: 'wrap', gap: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="subtitle1">Expenses</Typography>
                        <IconButton
                          size="small"
                          onClick={() => setShowZeroExpenseRows((v) => !v)}
                          title={showZeroExpenseRows ? 'Hide zero expense rows' : 'Show zero expense rows'}
                          aria-label={showZeroExpenseRows ? 'Hide zero expense rows' : 'Show zero expense rows'}
                        >
                          {showZeroExpenseRows ? <VisibilityOutlinedIcon fontSize="small" /> : <ClosedEyeOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <FinanceColumnMenu
                          labelId="fin-cols-personal-expense"
                          label="Columns"
                          prefs={columnPrefsPersonalExpense}
                          onPrefsChange={setColumnPrefsPersonalExpense}
                          options={[
                            { id: 'name', label: 'Name' },
                            { id: 'amount', label: 'Amount' },
                            { id: 'due', label: 'Due' },
                            { id: 'category', label: 'Category' },
                            { id: 'billDate', label: 'Bill generation date' },
                            { id: 'graceDays', label: 'Grace period (days)' },
                          ]}
                        />
                        {canEdit && (
                          <Button size="small" variant="outlined" onClick={() => { setEditingFinanceRow(null); setAddExpenseOpen(true); }}>Add expense</Button>
                        )}
                      </Stack>
                    </Stack>
                    <TableContainer sx={{ overflow: 'visible' }}>
                      <Table size="small" sx={{ width: 'max-content', minWidth: '100%' }}>
                        <TableHead>
                          <TableRow>
                            {showStatusCheckbox && <TableCell sx={{ width: 34, maxWidth: 34, px: 0 }} />}
                            {showStatusCheckbox && <TableCell sx={{ width: 34, maxWidth: 34, px: 0 }} />}
                            {columnPrefsPersonalExpense.name ? (
                              <SortableHeaderCell column="name" label="Name" sortValue={personalExpenseSort} setSort={setPersonalExpenseSort} sx={{ width: '55%' }} />
                            ) : null}
                            {columnPrefsPersonalExpense.category ? (
                              <SortableHeaderCell column="category" label="Category" sortValue={personalExpenseSort} setSort={setPersonalExpenseSort} />
                            ) : null}
                            {columnPrefsPersonalExpense.amount ? (
                              <SortableHeaderCell column="amount" label="Amount" sortValue={personalExpenseSort} setSort={setPersonalExpenseSort} align="right" sx={{ width: '30%' }} />
                            ) : null}
                            {columnPrefsPersonalExpense.billDate ? (
                              <SortableHeaderCell column="billDate" label="Bill generation date" sortValue={personalExpenseSort} setSort={setPersonalExpenseSort} />
                            ) : null}
                            {columnPrefsPersonalExpense.graceDays ? (
                              <SortableHeaderCell column="graceDays" label="Grace period (days)" sortValue={personalExpenseSort} setSort={setPersonalExpenseSort} />
                            ) : null}
                            {columnPrefsPersonalExpense.due ? (
                              <SortableHeaderCell column="due" label="Due" sortValue={personalExpenseSort} setSort={setPersonalExpenseSort} sx={{ width: '15%' }} />
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visiblePersonalExpensesSorted.map((row) => (
                            <EditableRow
                              key={rowKey(row)}
                              row={row}
                              editingField={rowProps.getEditingField(row)}
                              canEdit={rowProps.canEdit}
                              showStatusCheckbox={rowProps.showStatusCheckbox}
                              showNameColumn={columnPrefsPersonalExpense.name}
                              showAmountColumn={columnPrefsPersonalExpense.amount}
                              showCategoryColumn={columnPrefsPersonalExpense.category}
                              showBillDateColumn={columnPrefsPersonalExpense.billDate}
                              showGraceDaysColumn={columnPrefsPersonalExpense.graceDays}
                              showDueColumn={columnPrefsPersonalExpense.due}
                              dueDateDisplay={row.dueDateDisplay}
                              editName={rowProps.editName}
                              editAmount={rowProps.editAmount}
                              editCategory={rowProps.editCategory}
                              editDueDay={rowProps.editDueDay}
                              onStartEdit={rowProps.onStartEdit}
                              onEditNameChange={rowProps.onEditNameChange}
                              onEditAmountChange={rowProps.onEditAmountChange}
                              onEditCategoryChange={rowProps.onEditCategoryChange}
                              onEditDueDayChange={rowProps.onEditDueDayChange}
                              onSave={rowProps.onSave}
                              onCancel={rowProps.onCancel}
                              onStatusChange={rowProps.onStatusChange}
                              onDelete={handleDelete}
                              onOpenEditDialog={openEditPersonalExpense}
                            />
                          ))}
                          {isLatestMonthView && pendingExpensesFromPrevious.length > 0 && (
                            <>
                              <TableRow>
                                <TableCell colSpan={personalExpenseColSpan} sx={{ fontWeight: 600, pt: 2, pb: 0.5 }}>Pending from Previous months</TableCell>
                              </TableRow>
                              {visiblePendingExpensesFromPreviousSorted.map((row) => (
                                <EditableRow
                                  key={rowKey(row)}
                                  row={row}
                                  editingField={rowProps.getEditingField(row)}
                                  canEdit={rowProps.canEdit}
                                  showStatusCheckbox={rowProps.showStatusCheckbox}
                                  showNameColumn={columnPrefsPersonalExpense.name}
                                  showAmountColumn={columnPrefsPersonalExpense.amount}
                                  showCategoryColumn={columnPrefsPersonalExpense.category}
                                  showBillDateColumn={columnPrefsPersonalExpense.billDate}
                                  showGraceDaysColumn={columnPrefsPersonalExpense.graceDays}
                                  showDueColumn={columnPrefsPersonalExpense.due}
                                  dueDateDisplay={row.dueDateDisplay}
                                  editName={rowProps.editName}
                                  editAmount={rowProps.editAmount}
                                  editCategory={rowProps.editCategory}
                                  editDueDay={rowProps.editDueDay}
                                  onStartEdit={rowProps.onStartEdit}
                                  onEditNameChange={rowProps.onEditNameChange}
                                  onEditAmountChange={rowProps.onEditAmountChange}
                                  onEditCategoryChange={rowProps.onEditCategoryChange}
                                  onEditDueDayChange={rowProps.onEditDueDayChange}
                                  onSave={rowProps.onSave}
                                  onCancel={rowProps.onCancel}
                                  onStatusChange={rowProps.onStatusChange}
                                  onDelete={handleDelete}
                                  onOpenEditDialog={openEditPersonalExpense}
                                />
                              ))}
                            </>
                          )}
                          {visiblePersonalExpensesSorted.length === 0 && !(isLatestMonthView && visiblePendingExpensesFromPreviousSorted.length > 0) && (
                            <TableRow>
                              <TableCell colSpan={personalExpenseColSpan} sx={{ color: 'text.secondary' }}>
                                No expenses in range
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                  </Box>
                </Box>

                {chartData && chartData.seriesLabels.length > 0 && (
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Charts</Typography>
                    <Stack spacing={4}>
                      {(() => {
                        const incomeKeys = Object.keys(chartData.incomeByEarner || {});
                        if (incomeKeys.length === 0) return null;
                        const incomeChartData = chartData.seriesLabels.map((month, i) => {
                          const point = { month };
                          incomeKeys.forEach((earner) => {
                            point[earner] = (chartData.incomeByEarner[earner][i] || 0);
                          });
                          return point;
                        });
                        const CHART_COLORS = ['#2e7d32', '#1565c0', '#c62828', '#6a1b9a', '#ef6c00', '#00838f', '#7b1fa2', '#558b2f'];
                        return (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Income by earner</Typography>
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={incomeChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => v.toLocaleString()} />
                                <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                                <Legend />
                                {incomeKeys.map((key, idx) => (
                                  <Bar key={key} dataKey={key} stackId="income" fill={CHART_COLORS[idx % CHART_COLORS.length]} name={key} />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        );
                      })()}
                      {(() => {
                        const ccKeys = Object.keys(chartData.creditCardSpend || {});
                        if (ccKeys.length === 0) return null;
                        const ccChartData = chartData.seriesLabels.map((month, i) => {
                          const point = { month };
                          ccKeys.forEach((name) => {
                            point[name] = (chartData.creditCardSpend[name][i] || 0);
                          });
                          return point;
                        });
                        const CC_COLORS = ['#b71c1c', '#d84315', '#bf360c', '#e64a19', '#ff5722'];
                        return (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Credit card spend</Typography>
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={ccChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => v.toLocaleString()} />
                                <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                                <Legend />
                                {ccKeys.map((key, idx) => (
                                  <Bar key={key} dataKey={key} stackId="cc" fill={CC_COLORS[idx % CC_COLORS.length]} name={key} />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        );
                      })()}
                      {(() => {
                        const flowData = chartData.seriesLabels.map((month, i) => ({
                          month,
                          Income: chartData.totalIncomeByMonth?.[i] ?? 0,
                          Expenses: chartData.totalExpensesByMonth?.[i] ?? 0,
                        }));
                        return (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Income vs expenses</Typography>
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={flowData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => v.toLocaleString()} />
                                <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                                <Legend />
                                <Bar dataKey="Income" fill="#2e7d32" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Expenses" fill="#c62828" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        );
                      })()}
                    </Stack>
                  </Paper>
                )}
              </>
            )}

            {subTab === 'tax' && (
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1">Financial year</Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={taxFy}
                        onChange={(e) => setTaxFy(e.target.value)}
                      >
                        {(taxSlabs ? Object.keys(taxSlabs).sort() : ['2021-22', '2022-23', '2023-24', '2024-25', '2025-26']).map((fy) => (
                          <MenuItem key={fy} value={fy}>{fy}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {!taxLoading && taxData && (taxData.totalGrossSalary != null || taxData.totalGrossContractor != null) && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="body1">
                        <strong>Total Gross Salary:</strong>{' '}
                        {Number(taxData.totalGrossSalary ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Total Gross Contractor Invoices:</strong>{' '}
                        {Number(taxData.totalGrossContractor ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Stack>
                  )}
                  {taxLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={32} /></Box>}
                  {!taxLoading && taxData && taxData.byEarner && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Earner</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Gross income</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Taxable income</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Tax liability</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>TDS</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Pending tax</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {taxData.byEarner.map((r) => (
                            <TableRow key={r.earner}>
                              <TableCell>{r.earner}</TableCell>
                              <TableCell align="right">{Number(r.gross).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                              <TableCell align="right">{Number(r.taxable).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                              <TableCell align="right">{Number(r.tax).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                              <TableCell align="right">{Number(r.tds).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: r.pendingTax > 0 ? 600 : undefined }}>{Number(r.pendingTax).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {!taxLoading && taxData && (!taxData.byEarner || taxData.byEarner.length === 0) && (
                    <Typography color="text.secondary">No income data for this FY.</Typography>
                  )}
                </Stack>
              </Paper>
            )}

            {subTab === 'loans' && (
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1">Loans</Typography>
                    <Button size="small" variant="outlined" onClick={() => setAddLoanOpen(true)}>Add loan</Button>
                  </Box>
                  {!loansLoading && loans.length > 0 && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="body1">
                        <strong>Total Foreclosure Amount:</strong>{' '}
                        {loans.reduce((s, l) => s + Number(l.foreclosure_amount ?? 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Total EMI:</strong>{' '}
                        {loans.reduce((s, l) => s + Number(l.monthly_emi ?? 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Stack>
                  )}
                  {loansLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={32} /></Box>}
                  {!loansLoading && loans.length > 0 && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Start date</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Principal</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Interest rate %</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Last EMI date</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Foreclosure amount</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Foreclosure %</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Interest savings</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loans.map((loan) => (
                            <TableRow key={loan.id}>
                              <TableCell>{loan.name}</TableCell>
                              <TableCell>{loan.start_date}</TableCell>
                              <TableCell align="right">{Number(loan.principal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                              <TableCell align="right">{Number(loan.interest_rate).toFixed(2)}</TableCell>
                              <TableCell>{loan.last_emi_date ?? '—'}</TableCell>
                              <TableCell align="right">{Number(loan.foreclosure_amount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                              <TableCell align="right">{loan.foreclosure_rate != null ? `${Number(loan.foreclosure_rate).toFixed(1)}%` : '—'}</TableCell>
                              <TableCell align="right">{Number(loan.interest_savings ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <IconButton size="small" aria-label="View ledger" onClick={() => openLedger(loan.id, loan.name)} title="View ledger">
                                    <ReceiptLongIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                  <IconButton size="small" aria-label="Delete loan" onClick={() => deleteLoan(loan.id)} title="Delete" sx={{ color: 'error.main' }}>
                                    <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {!loansLoading && loans.length === 0 && (
                    <Typography color="text.secondary">No loans. Add a loan to get started.</Typography>
                  )}
                </Stack>
              </Paper>
            )}

            <Dialog open={addLoanOpen} onClose={() => setAddLoanOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Add loan</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <TextField label="Name" value={addLoanName} onChange={(e) => setAddLoanName(e.target.value)} fullWidth required />
                  <TextField label="First EMI date" type="date" value={addLoanStartDate} onChange={(e) => setAddLoanStartDate(e.target.value)} fullWidth required InputLabelProps={{ shrink: true }} helperText="Date of the first EMI (due date)" />
                  <TextField label="Principal" type="number" value={addLoanPrincipal} onChange={(e) => setAddLoanPrincipal(e.target.value)} fullWidth required inputProps={{ min: 0, step: 0.01 }} />
                  <TextField label="Interest rate (%)" type="number" value={addLoanInterestRate} onChange={(e) => setAddLoanInterestRate(e.target.value)} fullWidth required inputProps={{ min: 0, step: 0.01 }} />
                  <TextField label="Tenure (months)" type="number" value={addLoanTenure} onChange={(e) => setAddLoanTenure(e.target.value)} fullWidth required inputProps={{ min: 1, step: 1 }} />
                  <TextField label="EMI" type="number" value={addLoanEmi} onChange={(e) => setAddLoanEmi(e.target.value)} fullWidth placeholder="Optional: actual EMI if higher than calculated" helperText="Optional: use when your actual EMI differs from the formula (e.g. bank rounds up)" inputProps={{ min: 0, step: 0.01 }} />
                  <TextField label="Foreclosure rate (%)" type="number" value={addLoanForeclosureRate} onChange={(e) => setAddLoanForeclosureRate(e.target.value)} fullWidth placeholder="e.g. 2 for 2% penalty" helperText="Optional: % charged on outstanding if you foreclose early" inputProps={{ min: 0, step: 0.01 }} />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAddLoanOpen(false)}>Cancel</Button>
                <Button onClick={handleAddLoan} variant="contained" disabled={!addLoanName.trim() || !addLoanStartDate || !addLoanPrincipal || addLoanInterestRate === '' || !addLoanTenure}>Add</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={ledgerOpen} onClose={() => setLedgerOpen(false)} maxWidth="md" fullWidth>
              <DialogTitle>{ledgerData?.loanName ? `Ledger: ${ledgerData.loanName}` : 'Ledger'}</DialogTitle>
              <DialogContent>
                {ledgerLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>}
                {!ledgerLoading && ledgerData?.ledger && (
                  <TableContainer sx={{ maxHeight: 440 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>EMI date</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Opening</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>EMI</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Principal</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Interest</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Closing</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ledgerData.ledger.map((row) => (
                          <TableRow key={row.month}>
                            <TableCell>{row.month}</TableCell>
                            <TableCell>{row.emi_date}</TableCell>
                            <TableCell align="right">{Number(row.opening).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell align="right">{Number(row.emi).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell align="right">{Number(row.principal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell align="right">{Number(row.interest).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell align="right">{Number(row.closing).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setLedgerOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {/* CUSTOM finance type — simple income/expenses view with dates */}
        {activeFinanceType !== 'personal' && (
          <>
            {/* Add income dialog */}
            {!isPlanTab && (
            <Dialog open={addCustomIncomeOpen} onClose={resetCustomIncomeForm} maxWidth="xs" fullWidth>
              <DialogTitle>{editingFinanceRow?.kind === 'custom-income' ? 'Edit income' : 'Add income'}</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <TextField label="Name" value={customIncomeName} onChange={(e) => setCustomIncomeName(e.target.value)} fullWidth required autoFocus />
                  <TextField label="Amount" type="number" value={customIncomeAmount} onChange={(e) => setCustomIncomeAmount(e.target.value)} fullWidth required inputProps={{ min: 0, step: 0.01 }} />
                  <TextField label="Date" type="date" value={customIncomeDate} onChange={(e) => setCustomIncomeDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} helperText="Optional" />
                  <TextField label="Category" value={customIncomeCategory} onChange={(e) => setCustomIncomeCategory(e.target.value)} fullWidth placeholder="Optional" />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={resetCustomIncomeForm}>Cancel</Button>
                <Button onClick={handleAddCustomIncome} variant="contained" disabled={!customIncomeName.trim() || !customIncomeAmount}>
                  {editingFinanceRow?.kind === 'custom-income' ? 'Save' : 'Add'}
                </Button>
              </DialogActions>
            </Dialog>
            )}

            {/* Add/Edit expense dialog */}
            <Dialog open={addCustomExpenseOpen} onClose={resetCustomExpenseForm} maxWidth="xs" fullWidth>
              <DialogTitle>{editingFinanceRow?.kind === 'custom-expense' ? 'Edit expense' : 'Add expense'}</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <TextField label="Name" value={customExpenseName} onChange={(e) => setCustomExpenseName(e.target.value)} fullWidth required autoFocus />
                  <TextField label="Amount" type="number" value={customExpenseAmount} onChange={(e) => setCustomExpenseAmount(e.target.value)} fullWidth required inputProps={{ min: 0, step: 0.01 }} />
                  <TextField label="Date" type="date" value={customExpenseDate} onChange={(e) => setCustomExpenseDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} helperText={isPlanTab ? 'Target date — used to calculate monthly savings needed' : 'Optional'} />
                  {!isPlanTab && (
                    <TextField label="Category" value={customExpenseCategory} onChange={(e) => setCustomExpenseCategory(e.target.value)} fullWidth placeholder="Optional" />
                  )}
                  {isPlanTab && (
                    <TextField
                      select
                      label="Pay via"
                      fullWidth
                      size="small"
                      value={customExpensePaymentMode === 'cash' ? 'cash' : 'credit_card'}
                      onChange={(e) => setCustomExpensePaymentMode(e.target.value)}
                      helperText="Credit card gets extra runway till the bill due date"
                    >
                      <MenuItem value="credit_card">Credit card</MenuItem>
                      <MenuItem value="cash">Cash</MenuItem>
                    </TextField>
                  )}
                  {isMarriageTab && (
                    <>
                      <TextField
                        select
                        label="Payment mode"
                        fullWidth
                        size="small"
                        value={customExpensePaymentMode === 'credit_card' ? 'credit_card' : 'cash'}
                        onChange={(e) => setCustomExpensePaymentMode(e.target.value)}
                      >
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="credit_card">Credit card</MenuItem>
                      </TextField>
                      <TextField label="Paid by" value={customExpensePaymentBy} onChange={(e) => setCustomExpensePaymentBy(e.target.value)} fullWidth placeholder="Who paid" />
                    </>
                  )}
                  {!isPlanTab && (
                    <>
                      <TextField
                        label="Bill generation date (day)"
                        type="number"
                        inputProps={{ min: 1, max: 31 }}
                        value={customExpenseBillDate}
                        onChange={(e) => setCustomExpenseBillDate(e.target.value)}
                        fullWidth
                        placeholder="1–31"
                        helperText="Optional"
                      />
                      <TextField
                        label="Grace period (days)"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={customExpenseGraceDays}
                        onChange={(e) => setCustomExpenseGraceDays(e.target.value)}
                        fullWidth
                        placeholder="e.g. 45"
                        helperText="Optional"
                      />
                    </>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={resetCustomExpenseForm}>Cancel</Button>
                <Button onClick={handleAddCustomExpense} variant="contained" disabled={!customExpenseName.trim() || !customExpenseAmount}>
                  {editingFinanceRow?.kind === 'custom-expense' ? 'Save' : 'Add'}
                </Button>
              </DialogActions>
            </Dialog>

            {customLoading && (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
              </Paper>
            )}

            {!customLoading && customEntries && (() => {
              let cTotalIncome = 0;
              let cPendingIncome = 0;
              const incomeCompletedByName = {};
              let cTotalExpenses = 0;
              let cPendingExpenses = 0;
              for (const r of customEntries.income) {
                const amt = Number(r.amount);
                cTotalIncome += amt;
                if (r.status === 'completed') {
                  const key = r.name && String(r.name).trim() ? String(r.name).trim() : 'Others';
                  incomeCompletedByName[key] = (incomeCompletedByName[key] || 0) + amt;
                } else {
                  cPendingIncome += amt;
                }
              }
              for (const r of customEntries.expenses) {
                cTotalExpenses += Number(r.amount);
                if (r.status !== 'completed') cPendingExpenses += Number(r.amount);
              }
              const cBalance = cTotalIncome - cTotalExpenses;
              const engagementPaidBy = {};
              if (isMarriageTab) {
                for (const r of customEntries.expenses) {
                  if (r.status !== 'completed') continue;
                  const label = r.payment_by && String(r.payment_by).trim() ? String(r.payment_by).trim() : 'Others';
                  engagementPaidBy[label] = (engagementPaidBy[label] || 0) + Number(r.amount);
                }
              }
              const engagementPaidByNames = Object.keys(engagementPaidBy);
              engagementPaidByNames.sort((a, b) => a.localeCompare(b));
              const incomeNameKeys = Object.keys(incomeCompletedByName);
              incomeNameKeys.sort((a, b) => a.localeCompare(b));
              const customIncomeByCategory = {};
              for (let ii = 0; ii < customEntries.income.length; ii += 1) {
                const r = customEntries.income[ii];
                const lab = financesCategoryKey(r.category) || '(Uncategorized)';
                customIncomeByCategory[lab] = (customIncomeByCategory[lab] || 0) + Number(r.amount);
              }
              const customExpensesByCategory = {};
              for (let ei = 0; ei < customEntries.expenses.length; ei += 1) {
                const r = customEntries.expenses[ei];
                const lab = financesCategoryKey(r.category) || '(Uncategorized)';
                customExpensesByCategory[lab] = (customExpensesByCategory[lab] || 0) + Number(r.amount);
              }
              const customIncomeCatKeys = Object.keys(customIncomeByCategory);
              customIncomeCatKeys.sort((a, b) => a.localeCompare(b));
              const customExpenseCatKeys = Object.keys(customExpensesByCategory);
              customExpenseCatKeys.sort((a, b) => a.localeCompare(b));
              const customExpenseColumnOptions = [
                { id: 'date', label: 'Date' },
                { id: 'name', label: 'Name' },
                { id: 'amount', label: 'Amount' },
                { id: 'category', label: 'Category' },
                { id: 'billDate', label: 'Bill generation date' },
                { id: 'graceDays', label: 'Grace period (days)' },
              ];
              if (isMarriageTab) {
                customExpenseColumnOptions.push({ id: 'paymentMode', label: 'Payment mode' });
                customExpenseColumnOptions.push({ id: 'paidBy', label: 'Paid by' });
              }
              if (isPlanTab) {
                const planCards = [];
                const planGoals = [];
                let planTotalNeeded = 0;
                let planTotalSaved = 0;
                let planTotalToSave = 0;
                let planDatelessCount = 0;
                for (const row of customExpensesSorted) {
                  const saved = planSavedForExpense(row.name, customEntries.income);
                  const paymentMode = row.payment_mode === 'cash' ? 'cash' : 'credit_card';
                  const plan = monthlySavingsNeeded(row.amount, saved.amount, row.date, paymentMode);
                  const inSummary = row.include_in_summary !== false;
                  if (inSummary) {
                    planTotalNeeded += Number(row.amount) || 0;
                    planTotalSaved += Number(saved.amount) || 0;
                    planTotalToSave += plan.remaining;
                    if (!plan.fullyFunded && plan.remaining > 0) {
                      if (plan.months == null) planDatelessCount += 1;
                      else planGoals.push({ id: row.id, name: row.name, remaining: plan.remaining, months: plan.months });
                    }
                  }
                  const editingName = customEditingRowId === row.id && customEditingField === 'name';
                  const editingAmount = customEditingRowId === row.id && customEditingField === 'amount';
                  const editingDate = customEditingRowId === row.id && customEditingField === 'date';
                  const editingSaved = planSavedEditExpenseId === row.id;
                  let monthlyLine = 'Set a date to see how much to save each month';
                  if (plan.fullyFunded) monthlyLine = 'Fully funded';
                  else if (plan.monthly == null) monthlyLine = 'Set a date to see how much to save each month';
                  else if (plan.months === 0) monthlyLine = `${formatRupees(plan.monthly)} needed now (due)`;
                  else monthlyLine = `${formatRupees(plan.monthly)}/month · ready by ${planSlotLabel(plan.months - 1)}`;
                  planCards.push(
                    <Paper
                      key={row.id}
                      sx={{
                        px: 1.75,
                        py: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        opacity: inSummary ? 1 : 0.72,
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {editingName ? (
                            <ClickAwayListener onClickAway={() => saveCustomEdit(row.id, 'name')}>
                              <TextField
                                size="small"
                                fullWidth
                                variant="standard"
                                value={customEditName}
                                onChange={(e) => setCustomEditName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveCustomEdit(row.id, 'name');
                                  if (e.key === 'Escape') cancelCustomEdit();
                                }}
                                InputProps={{ sx: { fontSize: '0.95rem', fontWeight: 600 } }}
                                autoFocus
                              />
                            </ClickAwayListener>
                          ) : (
                            <Typography
                              variant="subtitle2"
                              noWrap
                              sx={{ fontWeight: 600, cursor: 'pointer' }}
                              onClick={() => startCustomEdit(row, 'name')}
                            >
                              {row.name}
                            </Typography>
                          )}
                        </Box>
                        <Switch
                          size="small"
                          checked={inSummary}
                          onChange={(e) => handlePlanIncludeInSummaryChange(row.id, e.target.checked)}
                          inputProps={{ 'aria-label': 'Include in summary' }}
                          sx={{ flexShrink: 0 }}
                        />
                        <IconButton size="small" aria-label="Delete" onClick={() => handleDeleteCustomRow(row.id)} sx={{ color: 'error.main', p: 0.5 }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>

                      <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                        <Box sx={{ minWidth: 72 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Amount</Typography>
                          {editingAmount ? (
                            <ClickAwayListener onClickAway={() => saveCustomEdit(row.id, 'amount')}>
                              <TextField
                                size="small"
                                type="number"
                                variant="standard"
                                value={customEditAmount}
                                onChange={(e) => setCustomEditAmount(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveCustomEdit(row.id, 'amount');
                                  if (e.key === 'Escape') cancelCustomEdit();
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{ sx: { fontSize: '0.875rem' } }}
                                sx={{ width: 96 }}
                                autoFocus
                              />
                            </ClickAwayListener>
                          ) : (
                            <Typography variant="body2" sx={{ cursor: 'pointer', lineHeight: 1.4 }} onClick={() => startCustomEdit(row, 'amount')}>
                              {formatRupees(row.amount)}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ minWidth: 88 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Due</Typography>
                          {editingDate ? (
                            <ClickAwayListener onClickAway={() => saveCustomEdit(row.id, 'date')}>
                              <TextField
                                size="small"
                                type="date"
                                variant="standard"
                                value={customEditDate}
                                onChange={(e) => setCustomEditDate(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveCustomEdit(row.id, 'date');
                                  if (e.key === 'Escape') cancelCustomEdit();
                                }}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ sx: { fontSize: '0.875rem' } }}
                                sx={{ width: 128 }}
                                autoFocus
                              />
                            </ClickAwayListener>
                          ) : (
                            <Typography variant="body2" sx={{ cursor: 'pointer', lineHeight: 1.4 }} onClick={() => startCustomEdit(row, 'date')}>
                              {row.date ? formatDate(row.date) : 'Set date'}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ minWidth: 72 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Saved</Typography>
                          {editingSaved ? (
                            <ClickAwayListener onClickAway={() => savePlanSavedAmount(row)}>
                              <TextField
                                size="small"
                                type="number"
                                variant="standard"
                                value={planSavedEditValue}
                                onChange={(e) => setPlanSavedEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') savePlanSavedAmount(row);
                                  if (e.key === 'Escape') {
                                    setPlanSavedEditExpenseId(null);
                                    setPlanSavedEditValue('');
                                  }
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{ sx: { fontSize: '0.875rem' } }}
                                sx={{ width: 96 }}
                                autoFocus
                              />
                            </ClickAwayListener>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ cursor: 'pointer', lineHeight: 1.4 }}
                              onClick={() => {
                                setPlanSavedEditExpenseId(row.id);
                                setPlanSavedEditValue(String(saved.amount || 0));
                              }}
                            >
                              {formatRupees(saved.amount)}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ minWidth: 88 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Pay via</Typography>
                          <Select
                            size="small"
                            variant="standard"
                            disableUnderline
                            value={paymentMode}
                            onChange={(e) => handlePlanPaymentModeChange(row.id, e.target.value)}
                            sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}
                          >
                            <MenuItem value="credit_card">Credit card</MenuItem>
                            <MenuItem value="cash">Cash</MenuItem>
                          </Select>
                        </Box>
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{
                          pt: 0.75,
                          borderTop: 1,
                          borderColor: 'divider',
                          color: plan.fullyFunded ? 'success.main' : 'text.secondary',
                          fontWeight: 500,
                          lineHeight: 1.35,
                        }}
                      >
                        {monthlyLine}
                      </Typography>
                    </Paper>
                  );
                }
                return (
                  <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1">Planned expenses</Typography>
                      <Button size="small" variant="outlined" onClick={() => { setEditingFinanceRow(null); setCustomExpensePaymentMode('credit_card'); setAddCustomExpenseOpen(true); }}>Add expense</Button>
                    </Stack>
                    {planCards.length > 0 && (() => {
                      const planSchedule = buildPlanSchedule(planGoals, parseFloat(planMinMonthly) || 0);
                      const comingMonthTotal = planSchedule.slots.length > 0 ? planSchedule.slots[0].total : 0;
                      const scheduleRows = [];
                      for (const slot of planSchedule.slots) {
                        const partLines = [];
                        for (const p of slot.parts) {
                          partLines.push(
                            <Typography key={p.id} variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {p.name} {formatRupees(p.amount)}
                            </Typography>
                          );
                        }
                        scheduleRows.push(
                          <TableRow key={slot.monthOffset}>
                            <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{planSlotLabel(slot.monthOffset)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                              {slot.total > 0 ? formatRupees(slot.total) : '—'}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {partLines.length > 0 ? partLines : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return (
                        <Paper sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                            <Typography variant="subtitle1">Summary</Typography>
                            <TextField
                              size="small"
                              type="number"
                              label="Min per month"
                              value={planMinMonthly}
                              onChange={(e) => setPlanMinMonthly(e.target.value)}
                              onBlur={savePlanMinMonthly}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') savePlanMinMonthly();
                              }}
                              inputProps={{ min: 0, step: 1000 }}
                              sx={{ width: 150 }}
                            />
                          </Stack>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">Total needed</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>{formatRupees(planTotalNeeded)}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">Saved so far</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>{formatRupees(planTotalSaved)}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">To be saved</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>{formatRupees(planTotalToSave)}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">Peak month</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>{formatRupees(planSchedule.peak)}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Coming month (due {planSchedule.slots.length > 0 ? planSlotLabel(0) : '—'})
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>{formatRupees(comingMonthTotal)}</Typography>
                            </Box>
                          </Stack>
                          {planDatelessCount > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              {planDatelessCount} goal{planDatelessCount > 1 ? 's' : ''} without a due date not scheduled.
                            </Typography>
                          )}
                          {scheduleRows.length > 0 && (
                            <Box sx={{ mt: 1.5, overflow: 'auto' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Schedule (deferred goals start later to keep each month as low as possible)
                              </Typography>
                              <Table size="small" sx={{ width: 'auto', minWidth: 320 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Save by</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Towards</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>{scheduleRows}</TableBody>
                              </Table>
                            </Box>
                          )}
                        </Paper>
                      );
                    })()}
                    {planCards.length === 0 && (
                      <Paper sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">No planned expenses yet. Add an expense with a target date to track monthly savings.</Typography>
                      </Paper>
                    )}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                        gap: 1.5,
                        alignItems: 'stretch',
                      }}
                    >
                      {planCards}
                    </Box>
                  </>
                );
              }
              return (
                <>
                  {/* Summary — same table style as personal tab */}
                  <Paper sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Summary</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" sx={{ overflow: 'auto' }}>
                      <Table size="small" sx={{ width: 'auto', minWidth: { xs: 140, sm: 200 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Income</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cPendingIncome > 0 && (
                            <TableRow>
                              <TableCell>Pending</TableCell>
                              <TableCell align="right">{cPendingIncome.toFixed(2)}</TableCell>
                            </TableRow>
                          )}
                          {incomeNameKeys.map((nm) => (
                            <TableRow key={nm}>
                              <TableCell>{nm}</TableCell>
                              <TableCell align="right">{Number(incomeCompletedByName[nm]).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                          {columnPrefsCustomIncome.category && customIncomeCatKeys.length > 0 ? (
                            <>
                              <TableRow>
                                <TableCell colSpan={2} sx={{ fontWeight: 600, pt: 1 }}>By category (all statuses)</TableCell>
                              </TableRow>
                              {customIncomeCatKeys.map((ck) => (
                                <TableRow key={`sum-ic-${ck}`}>
                                  <TableCell sx={{ pl: 2 }}>{ck}</TableCell>
                                  <TableCell align="right">{Number(customIncomeByCategory[ck]).toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                            </>
                          ) : null}
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{cTotalIncome.toFixed(2)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <Table size="small" sx={{ width: 'auto', minWidth: { xs: 140, sm: 200 } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Expenses</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cPendingExpenses > 0 && (
                            <TableRow>
                              <TableCell>Pending</TableCell>
                              <TableCell align="right">{cPendingExpenses.toFixed(2)}</TableCell>
                            </TableRow>
                          )}
                          {isMarriageTab && engagementPaidByNames.length > 0 && (
                            <>
                              <TableRow>
                                <TableCell colSpan={2} sx={{ fontWeight: 600, pt: 1, pb: 0.5 }}>Paid by</TableCell>
                              </TableRow>
                              {engagementPaidByNames.map((name) => (
                                <TableRow key={name}>
                                  <TableCell sx={{ pl: 2 }}>{name}</TableCell>
                                  <TableCell align="right">{Number(engagementPaidBy[name]).toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                            </>
                          )}
                          {columnPrefsCustomExpense.category && customExpenseCatKeys.length > 0 ? (
                            <>
                              <TableRow>
                                <TableCell colSpan={2} sx={{ fontWeight: 600, pt: 1 }}>By category (all statuses)</TableCell>
                              </TableRow>
                              {customExpenseCatKeys.map((ck) => (
                                <TableRow key={`sum-ec-${ck}`}>
                                  <TableCell sx={{ pl: 2 }}>{ck}</TableCell>
                                  <TableCell align="right">{Number(customExpensesByCategory[ck]).toFixed(2)}</TableCell>
                                </TableRow>
                              ))}
                            </>
                          ) : null}
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{cTotalExpenses.toFixed(2)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Stack>
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography component="span" sx={{ fontWeight: 600 }}>Balance</Typography>
                      <Typography component="span" sx={{ ml: 1, fontWeight: 600 }}>{cBalance.toFixed(2)}</Typography>
                    </Box>
                  </Paper>

                  {/* Income and Expenses tables — using shared EditableRow */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: { xs: 'stretch', md: 'center' },
                      width: '100%',
                      overflowX: 'auto',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(2, minmax(min-content, max-content))',
                        },
                        gap: 2,
                        alignItems: 'start',
                        width: { xs: '100%', md: 'max-content' },
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                    <Paper sx={{ overflow: 'visible', width: '100%' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="subtitle1">Income</Typography>
                          <IconButton
                            size="small"
                            onClick={() => setShowZeroIncomeRows((v) => !v)}
                            title={showZeroIncomeRows ? 'Hide zero income rows' : 'Show zero income rows'}
                            aria-label={showZeroIncomeRows ? 'Hide zero income rows' : 'Show zero income rows'}
                          >
                            {showZeroIncomeRows ? <VisibilityOutlinedIcon fontSize="small" /> : <ClosedEyeOutlinedIcon fontSize="small" />}
                          </IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <FinanceColumnMenu
                            labelId="fin-cols-custom-income"
                            label="Columns"
                            prefs={columnPrefsCustomIncome}
                            onPrefsChange={setColumnPrefsCustomIncome}
                            options={[
                              { id: 'date', label: 'Date' },
                              { id: 'name', label: 'Name' },
                              { id: 'amount', label: 'Amount' },
                              { id: 'category', label: 'Category' },
                            ]}
                          />
                          <Button size="small" variant="outlined" onClick={() => { setEditingFinanceRow(null); setAddCustomIncomeOpen(true); }}>Add income</Button>
                        </Stack>
                      </Stack>
                      <TableContainer sx={{ overflow: 'visible' }}>
                        <Table size="small" sx={{ width: 'max-content', minWidth: '100%' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: 34, maxWidth: 34, px: 0 }} />
                              <TableCell sx={{ width: 34, maxWidth: 34, px: 0 }} />
                              {columnPrefsCustomIncome.date ? (
                                <SortableHeaderCell column="date" label="Date" sortValue={customIncomeSort} setSort={setCustomIncomeSort} />
                              ) : null}
                              {columnPrefsCustomIncome.name ? (
                                <SortableHeaderCell column="name" label="Name" sortValue={customIncomeSort} setSort={setCustomIncomeSort} />
                              ) : null}
                              {columnPrefsCustomIncome.category ? (
                                <SortableHeaderCell column="category" label="Category" sortValue={customIncomeSort} setSort={setCustomIncomeSort} />
                              ) : null}
                              {columnPrefsCustomIncome.amount ? (
                                <SortableHeaderCell column="amount" label="Amount" sortValue={customIncomeSort} setSort={setCustomIncomeSort} align="right" />
                              ) : null}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {visibleCustomIncomeSorted.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={customIncomeColSpan} sx={{ color: 'text.secondary' }}>No income entries</TableCell>
                              </TableRow>
                            )}
                            {visibleCustomIncomeSorted.map((row) => (
                              <EditableRow
                                key={row.id}
                                row={row}
                                editingField={getCustomIncomeEditingField(row)}
                                {...customIncomeRowSpread}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    <Paper sx={{ overflow: 'visible', width: '100%' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="subtitle1">Expenses</Typography>
                          <IconButton
                            size="small"
                            onClick={() => setShowZeroExpenseRows((v) => !v)}
                            title={showZeroExpenseRows ? 'Hide zero expense rows' : 'Show zero expense rows'}
                            aria-label={showZeroExpenseRows ? 'Hide zero expense rows' : 'Show zero expense rows'}
                          >
                            {showZeroExpenseRows ? <VisibilityOutlinedIcon fontSize="small" /> : <ClosedEyeOutlinedIcon fontSize="small" />}
                          </IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <FinanceColumnMenu
                            labelId="fin-cols-custom-expense"
                            label="Columns"
                            prefs={columnPrefsCustomExpense}
                            onPrefsChange={setColumnPrefsCustomExpense}
                            options={customExpenseColumnOptions}
                          />
                          <Button size="small" variant="outlined" onClick={() => { setEditingFinanceRow(null); setAddCustomExpenseOpen(true); }}>Add expense</Button>
                        </Stack>
                      </Stack>
                      <TableContainer sx={{ overflow: 'visible' }}>
                        <Table size="small" sx={{ width: 'max-content', minWidth: '100%' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: 34, maxWidth: 34, px: 0 }} />
                              <TableCell sx={{ width: 34, maxWidth: 34, px: 0 }} />
                              {columnPrefsCustomExpense.date ? (
                                <SortableHeaderCell column="date" label="Date" sortValue={customExpenseSort} setSort={setCustomExpenseSort} />
                              ) : null}
                              {columnPrefsCustomExpense.name ? (
                                <SortableHeaderCell column="name" label="Name" sortValue={customExpenseSort} setSort={setCustomExpenseSort} />
                              ) : null}
                              {columnPrefsCustomExpense.category ? (
                                <SortableHeaderCell column="category" label="Category" sortValue={customExpenseSort} setSort={setCustomExpenseSort} />
                              ) : null}
                              {columnPrefsCustomExpense.amount ? (
                                <SortableHeaderCell column="amount" label="Amount" sortValue={customExpenseSort} setSort={setCustomExpenseSort} align="right" />
                              ) : null}
                              {columnPrefsCustomExpense.billDate ? (
                                <SortableHeaderCell column="billDate" label="Bill generation date" sortValue={customExpenseSort} setSort={setCustomExpenseSort} />
                              ) : null}
                              {columnPrefsCustomExpense.graceDays ? (
                                <SortableHeaderCell column="graceDays" label="Grace period (days)" sortValue={customExpenseSort} setSort={setCustomExpenseSort} />
                              ) : null}
                              {isMarriageTab && columnPrefsCustomExpense.paymentMode ? (
                                <SortableHeaderCell column="paymentMode" label="Payment mode" sortValue={customExpenseSort} setSort={setCustomExpenseSort} />
                              ) : null}
                              {isMarriageTab && columnPrefsCustomExpense.paidBy ? (
                                <SortableHeaderCell column="paidBy" label="Paid by" sortValue={customExpenseSort} setSort={setCustomExpenseSort} />
                              ) : null}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {visibleCustomExpensesSorted.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={customExpenseColSpan} sx={{ color: 'text.secondary' }}>No expense entries</TableCell>
                              </TableRow>
                            )}
                            {visibleCustomExpensesSorted.map((row) => (
                              <EditableRow
                                key={row.id}
                                row={row}
                                editingField={getCustomExpenseEditingField(row)}
                                {...customExpenseRowSpread}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                    </Box>
                  </Box>
                </>
              );
            })()}
          </>
        )}

      </Stack>
    </Page>
  );
}
